import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import FounderPill from "@/components/FounderPill";
import FounderHeadStartOverlay from "@/components/FounderHeadStartOverlay";
import HotLotBadge from "@/components/HotLotBadge";
import LiveStreamAccessGate from "@/components/LiveStreamAccessGate";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, apiError, SITE_ORIGIN } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  BroadcastIcon,
  PaperPlaneTiltIcon,
  UsersThreeIcon,
  TimerIcon,
  SignInIcon,
  SignOutIcon,
  MagnifyingGlassIcon,
  SpeakerHighIcon,
  SpeakerSlashIcon,
  ShareFatIcon,
  StarIcon,
  StorefrontIcon,
  WalletIcon,
  ChatCircleDotsIcon,
  XIcon,
  SparkleIcon,
  ArrowsClockwiseIcon,
} from "@phosphor-icons/react";
import TrustTierBadge from "@/components/TrustTierBadge";
import FollowButton from "@/components/FollowButton";
import BookmarkButton from "@/components/BookmarkButton";
import StreamVideo from "@/components/StreamVideo";
import CinemaStage from "@/components/CinemaStage";
import LiveTapLayer from "@/components/LiveTapLayer";
import TipSheet from "@/components/TipSheet";
import ShareSheet from "@/components/ShareSheet";
import { TipToastQueue } from "@/components/TipToast";
import AdminBadge from "@/components/AdminBadge";
import VerifiedBadge from "@/components/VerifiedBadge";
import SellerAvatar from "@/components/SellerAvatar";
import StreamRecapModal from "@/components/StreamRecapModal";
import ViewerListPopover from "@/components/ViewerListPopover";
import AICoHostPanel from "@/components/AICoHostPanel";
import AICoHostPlayer from "@/components/AICoHostPlayer";
import { GraderModal, GradeChatMessage } from "@/components/GraderModal";
import FirePill from "@/components/FirePill";
import ReportDialog from "@/components/ReportDialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import StreamModManager from "@/components/StreamModManager";
import ShopHub from "@/components/ShopHub";
import QuickAddListingModal from "@/components/QuickAddListingModal";
import ImportFromShopSheet from "@/components/ImportFromShopSheet";
import AutoDetectPopup from "@/components/AutoDetectPopup";
import PlusUpgradeSheet from "@/components/PlusUpgradeSheet";
import { useAutoDetect } from "@/hooks/useAutoDetect";
import SeoHead, { liveStreamLd, breadcrumbLd } from "@/components/SeoHead";
import ChatMessageModActions from "@/components/ChatMessageModActions";
import UserNamePopover from "@/components/UserNamePopover";
import GiveawayBanner, { StartGiveawayDialog } from "@/components/GiveawayBanner";
import WalletSheet from "@/components/WalletSheet";
import WinCelebration from "@/components/WinCelebration";
import { AnimatePresence } from "framer-motion";
import { FlagIcon, ShieldStarIcon, PushPinIcon, DotsThreeIcon, GiftIcon, HeartStraightIcon, CurrencyDollarIcon, SmileyIcon, DownloadSimpleIcon } from "@phosphor-icons/react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";

const fileUrl = (path) =>
  path ? `${SITE_ORIGIN}/api/files/${path}` : null;

export default function LiveStream() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stream, setStream] = useState(null);
  const [reportStreamOpen, setReportStreamOpen] = useState(false);
  const [streamMods, setStreamMods] = useState([]);
  const [showModManager, setShowModManager] = useState(false);
  const [showShopHub, setShowShopHub] = useState(false);
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  // Whatnot-style bulk import from the seller's existing shop inventory.
  // Opens an `ImportFromShopSheet` overlay that grids the seller's
  // active listings with checkboxes and a single "Import N" CTA.
  const [showImportShop, setShowImportShop] = useState(false);
  const [quickAddPrefill, setQuickAddPrefill] = useState(null);
  const [plusUpgradeOpen, setPlusUpgradeOpen] = useState(false);
  // "Bidoria sees an item" — seller-only auto-detect. Toggle lives on the
  // right rail (wand circle). The feature is **yearly-Plus only**: every
  // seller sees the rail circle, but tapping when ineligible pops the
  // PlusUpgradeSheet locked to the yearly plan instead of toggling.
  const [autoDetectEnabled, setAutoDetectEnabled] = useState(true);
  const [autoDetectCandidate, setAutoDetectCandidate] = useState(null);
  const [detectEligible, setDetectEligible] = useState(false);
  const [plusUpgradeReason, setPlusUpgradeReason] = useState(null);

  // Refresh eligibility whenever the user object changes (login,
  // post-purchase Plus activation, admin grant). Cheap call, cached on
  // backend.
  useEffect(() => {
    if (!user) { setDetectEligible(false); return; }
    let alive = true;
    api.get("/me/detect-status").then((r) => {
      if (alive) setDetectEligible(!!r?.data?.eligible);
    }).catch(() => { if (alive) setDetectEligible(false); });
    return () => { alive = false; };
  }, [user]);
  const [graderOpen, setGraderOpen] = useState(false);
  // Cinema 4K upsell — show a "4K" rail button to sellers when Mux is
  // configured but they don't yet have Plus. Tap → /plus checkout.
  const [muxEnabled, setMuxEnabled] = useState(false);
  const [isCinemaEligible, setIsCinemaEligible] = useState(false);
  // Whatnot-style "Hot Lot" pulse — server emits `lot.heat` WS events
  // on tier transitions (cold/active/hot/frenzy) based on rolling
  // bid velocity. We use the level to drive a pulsing badge + glow on
  // the pinned-lot card so viewers feel the bidding war in real time.
  const [lotHeat, setLotHeat] = useState("cold");
  const heatSoundRef = React.useRef(0);
  const [pinnedMessage, setPinnedMessage] = useState(null);
  const [messages, setMessages] = useState([]);

  // Whatnot-style immersive mode: swipe LEFT on the stream to hide the
  // chat overlay, comment composer, mobile right-rail and (on desktop)
  // the Shop + Chat side panels so the video can breathe full-bleed.
  // Swipe RIGHT to restore. State + handlers live here so a single
  // gesture on `<main>` controls every dependent layer.
  const [immersive, setImmersive] = useState(false);
  const [immersiveHintAt, setImmersiveHintAt] = useState(0);
  const swipeRef = React.useRef({ x: 0, y: 0, t: 0, active: false });

  const onSwipeStart = React.useCallback((e) => {
    // Skip when the gesture starts on an interactive element — we
    // don't want to fight the textarea, buttons, or video controls.
    const tag = (e.target?.tagName || "").toLowerCase();
    if (["input", "textarea", "select", "button", "a", "video"].includes(tag)) {
      swipeRef.current.active = false;
      return;
    }
    const t = e.touches?.[0];
    if (!t) return;
    swipeRef.current = { x: t.clientX, y: t.clientY, t: Date.now(), active: true };
  }, []);

  const onSwipeEnd = React.useCallback((e) => {
    const s = swipeRef.current;
    if (!s.active) return;
    swipeRef.current.active = false;
    const t = e.changedTouches?.[0];
    if (!t) return;
    const dx = t.clientX - s.x;
    const dy = t.clientY - s.y;
    const dt = Date.now() - s.t;
    // Horizontal swipe: >60px, more horizontal than vertical, under 700ms.
    if (Math.abs(dx) < 60 || Math.abs(dx) < Math.abs(dy) * 1.4 || dt > 700) return;
    setImmersive((cur) => {
      const next = dx < 0; // left swipe → enter immersive, right → exit
      if (next === cur) return cur;
      // Show the "swipe right to bring chat back" hint for a few seconds
      // whenever we enter immersive so first-time users aren't stranded.
      if (next) setImmersiveHintAt(Date.now());
      return next;
    });
  }, []);

  // Auto-dismiss the swipe hint after 3.5s.
  useEffect(() => {
    if (!immersiveHintAt) return;
    const id = setTimeout(() => setImmersiveHintAt(0), 3500);
    return () => clearTimeout(id);
  }, [immersiveHintAt]);

  const showImmersiveHint = immersive && immersiveHintAt > 0;

  const [presenceEvents, setPresenceEvents] = useState([]);
  const [lastPresence, setLastPresence] = useState(null);
  // Slim live-activity ticker queue — pushes for new bids, joins, big-tap
  // double-tap "likes", and tips. Each entry auto-evicts after ~5.5s so the
  // bottom strip stays a single moving line of social proof, not a wall.
  const [activityFeed, setActivityFeed] = useState([]);
  // Seller-only: load the persisted last-5 unique joiners so when the
  // seller re-opens the stream they can see who's been popping in. Refetches
  // any time a new join event arrives so the list stays current.
  const refreshRecentJoiners = useCallback(async () => {
    if (!stream || !user || user.user_id !== stream.seller_id) return;
    try {
      const r = await api.get(`/streams/${stream.id}/recent-joiners`, { params: { limit: 5 } });
      setRecentJoiners(r.data?.items || []);
    } catch (_) { /* 403 for non-sellers, 404 for ended streams — both ok */ }
  }, [stream, user]);
  useEffect(() => { refreshRecentJoiners(); }, [refreshRecentJoiners]);

  // Lock the live stream page to PORTRAIT orientation. When a viewer
  // rotates their phone, the show stays upright — same as platform /
  // TikTok Live / IG Live. Rotating mid-show is jarring because the
  // chat, bid input, and right rail all become unreachable when the
  // layout reflows landscape. Released on unmount so the rest of the
  // app stays free to rotate.
  // Uses the Web Screen Orientation API directly — works inside the
  // Capacitor WebView without a native plugin (no APK rebuild). On
  // browsers that don't support `screen.orientation.lock` (older iOS
  // Safari) the call simply rejects and we fall through silently —
  // those browsers don't rotate the WebView anyway.
  useEffect(() => {
    let locked = false;
    try {
      const orientation = (typeof screen !== "undefined" && screen.orientation) || null;
      if (orientation && typeof orientation.lock === "function") {
        orientation.lock("portrait")
          .then(() => { locked = true; })
          .catch(() => { /* gracefully ignore on iOS Safari etc. */ });
      }
    } catch (_) { /* ignore */ }
    return () => {
      if (!locked) return;
      try {
        const orientation = (typeof screen !== "undefined" && screen.orientation) || null;
        if (orientation && typeof orientation.unlock === "function") {
          orientation.unlock();
        }
      } catch (_) { /* ignore */ }
    };
  }, []);

  // Fetch Mux + Plus eligibility once per page load. Used to gate the
  // "Cinema 4K" rail tile (only shown to sellers when Mux is enabled
  // and they don't yet have Plus).
  useEffect(() => {
    let alive = true;
    api.get("/mux/config")
      .then(({ data }) => { if (alive) setMuxEnabled(!!data?.enabled); })
      .catch(() => { if (alive) setMuxEnabled(false); });
    return () => { alive = false; };
  }, []);
  useEffect(() => {
    if (!user || !stream || user.user_id !== stream.seller_id) return undefined;
    let alive = true;
    api.get("/me/cinema-status")
      .then(({ data }) => { if (alive) setIsCinemaEligible(!!data?.eligible); })
      .catch(() => { if (alive) setIsCinemaEligible(false); });
    return () => { alive = false; };
  }, [user, stream]);

  const pushActivity = useCallback((text, kind = "info", extras = {}) => {
    if (!text) return;
    const id = `act_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
    setActivityFeed((prev) => [...prev.slice(-4), { id, text, kind, ...extras }]);
    setTimeout(() => {
      setActivityFeed((prev) => prev.filter((x) => x.id !== id));
    }, 5500);
  }, []);
  const [liveViewers, setLiveViewers] = useState(0);
  const [chatInput, setChatInput] = useState("");
  const chatInputRef = useRef(null);
  const mobileChatInputRef = useRef(null);
  const [bidInput, setBidInput] = useState("");
  const [activeListing, setActiveListing] = useState(null);
  const [wsConnected, setWsConnected] = useState(false);
  const [videoBackend, setVideoBackend] = useState("none");
  const [cameraOn, setCameraOn] = useState(false);
  const [incomingTip, setIncomingTip] = useState(null);
  const [recap, setRecap] = useState(null);
  const [rightTab, setRightTab] = useState("chat");
  const [mobileShopOpen, setMobileShopOpen] = useState(false);
  const [mobileChatOpen, setMobileChatOpen] = useState(false);
  // Bottom-sheet "More" menu — collapses secondary actions (Share, Shop,
  // Mods, Cohost) off the right rail so it stays uncluttered on phones.
  const [mobileMoreOpen, setMobileMoreOpen] = useState(false);

  // ── Live giveaway state ────────────────────────────────────────────
  // `giveaway` is the currently active or recently-ended giveaway for this
  // stream. Set on mount via /api/streams/{id}/giveaways/active, updated in
  // real time by WS events (giveaway.started / entry / ended).
  const [giveaway, setGiveaway] = useState(null);
  const [startGiveawayOpen, setStartGiveawayOpen] = useState(false);
  // Auto-clear the ended-giveaway banner after ~10s so we don't permanently
  // cover the seller header.
  useEffect(() => {
    if (giveaway?.status !== "ended") return;
    const t = setTimeout(() => setGiveaway(null), 10000);
    return () => clearTimeout(t);
  }, [giveaway?.status, giveaway?.id]);
  const [muted, setMuted] = useState(false);
  const [shareSheetOpen, setShareSheetOpen] = useState(false);
  // live sale platforms-style in-stream wallet sheet — anchored to the right-rail
  // "Wallet" icon above Shop. Shows the user's credit balance + recent
  // ledger entries (`/api/me/credits`).
  const [walletSheetOpen, setWalletSheetOpen] = useState(false);
  // Controlled-open state for the Tip modal so the More-sheet Tip tile can
  // launch the same flow as the top-right green Tip pill (non-sellers).
  const [tipSheetOpen, setTipSheetOpen] = useState(false);
  // Desktop reactions popover state — anchored to the right-rail "smile"
  // circle. Replaces the old horizontal emoji pill at the bottom-center.
  const [reactionsOpen, setReactionsOpen] = useState(false);
  // The emoji the viewer has selected to react with. Synced to LiveTapLayer
  // via a window event (`bidorah:emoji-pick`) so the next double-tap
  // broadcast carries this emoji. Mirrors LiveTapLayer's internal default.
  const [pickedEmoji, setPickedEmoji] = useState("❤️");
  // Tier the CURRENT user has for the AI co-host (Bidoria) on THIS stream.
  // `full` (Plus subscriber) or `trial` (Founder grace window) unlocks
  // every Bidoria entry point; `locked` or null hides them entirely.
  // We gate at the entry point — the actual panel still has its own
  // tier-aware UI for defensive UX.
  const [cohostTier, setCohostTier] = useState(null);
  useEffect(() => {
    if (!id || !user) {
      setCohostTier(null);
      return;
    }
    let dead = false;
    api.get(`/ai-cohost/access`)
      .then((r) => { if (!dead) setCohostTier(r.data?.tier || "locked"); })
      .catch(() => { if (!dead) setCohostTier("locked"); });
    return () => { dead = true; };
  }, [id, user]);
  const bidoriaUnlocked = cohostTier === "full" || cohostTier === "trial";
  // Default saved card (if any) — drives the one-tap "Pay with •••• 1234"
  // label on the in-stream BUY button. Mirrors ProductDetail.jsx.
  const [defaultCard, setDefaultCard] = useState(null);
  // Auto-request browser fullscreen on the FIRST user gesture inside the
  // live stream. This is the only way to hide Chrome's URL bar / address
  // chrome on mobile web — the Fullscreen API requires a user gesture so
  // we attach a one-shot capture-phase listener.  No-op on Capacitor
  // native (system status bar is already hidden by RouteAwareChromeShell)
  // and no-op when already in fullscreen / on desktop.
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    if (window.matchMedia?.("(min-width: 768px)").matches) return undefined;
    if (window.Capacitor?.isNativePlatform?.()) return undefined;
    if (document.fullscreenElement) return undefined;
    const enter = () => {
      const el = document.documentElement;
      const req =
        el.requestFullscreen ||
        el.webkitRequestFullscreen ||
        el.mozRequestFullScreen ||
        el.msRequestFullscreen;
      try {
        // Some Safari versions throw synchronously on iOS; swallow.
        req?.call(el).catch?.(() => {});
      } catch { /* noop */ }
      window.removeEventListener("touchend", enter, true);
      window.removeEventListener("click", enter, true);
    };
    window.addEventListener("touchend", enter, true);
    window.addEventListener("click", enter, true);
    return () => {
      window.removeEventListener("touchend", enter, true);
      window.removeEventListener("click", enter, true);
      if (document.fullscreenElement && document.exitFullscreen) {
        try { document.exitFullscreen(); } catch { /* noop */ }
      }
    };
  }, []);
  // live sale platforms-style "YOU WON!" / "SOLD!" celebration overlay state.
  // Populated by the `pin.hammered` WS event; auto-clears after the
  // animation finishes (4.4s via the component itself).
  const [winCelebration, setWinCelebration] = useState(null);
  // Dev-only escape hatch: exposes `window.__bidoraTriggerWin(opts)` so we
  // can capture the celebration animation in screenshots without having
  // to win a real auction.  Strips itself on unmount.
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.__bidoraTriggerWin = (opts = {}) =>
      setWinCelebration({
        key: `dev-${Date.now()}`,
        amount: 250,
        listingTitle: "Air Jordan 1 Retro High 'Chicago'",
        winnerName: "leo_park",
        isSelf: true,
        cardLast4: "4242",
        ...opts,
      });
    return () => {
      try { delete window.__bidoraTriggerWin; } catch { /* noop */ }
    };
  }, []);
  useEffect(() => {
    if (!user) { setDefaultCard(null); return; }
    let alive = true;
    api.get("/me/wallet/payment-methods")
      .then(({ data }) => {
        if (!alive) return;
        const def = (data.cards || []).find((c) => c.is_default);
        setDefaultCard(def || null);
      })
      .catch(() => { if (alive) setDefaultCard(null); });
    return () => { alive = false; };
  }, [user]);
  const [tapCount, setTapCount] = useState(0);
  const [lastAILine, setLastAILine] = useState(null);
  const [adminEntryTrigger, setAdminEntryTrigger] = useState(null);
  // Seller-only: list of last 5 unique users who joined this stream, hydrated
  // from `GET /streams/{id}/recent-joiners` on mount and after each new join
  // event over the WS. Drives the "Recent visitors" popover on the seller card.
  const [recentJoiners, setRecentJoiners] = useState([]);
  const [showRecentJoiners, setShowRecentJoiners] = useState(false);
  const wsRef = useRef(null);
  const myConnIdRef = useRef(null);
  const chatRef = useRef(null);
  const isStaff = user && (user.role === "moderator" || user.role === "admin");
  const isOwner = user && stream && user.user_id === stream.seller_id;
  const isAppointedMod = user && streamMods.some((m) => m.user_id === user.user_id);
  // A "stream mod" can run the in-stream moderation actions (mute / kick /
  // delete / hide / pin). Anyone who isn't one of these still sees a clean
  // viewer chat.
  const isStreamMod = isOwner || isStaff || isAppointedMod;

  // Auto-detect — only fire when the seller is broadcasting their OWN
  // live stream. We bind the hook to the seller's local <video> element
  // (which StreamVideo tags `data-stream-video`).  The element only
  // exists after camera permission is granted, so we resolve it lazily
  // every render.
  // Resolve the seller's actively-playing local <video> so the
  // Auto-Detect hook can pull frames from it. We can't grab this
  // during render — the element doesn't exist until camera permission
  // is granted AND the underlying stream (mesh or LiveKit) attaches its
  // track. So we poll the DOM every 500 ms once the seller is live and
  // settle on whichever <video> is actually rendering pixels (has
  // non-zero videoWidth). Tested on both mesh and LiveKit paths.
  const [detectVideoEl, setDetectVideoEl] = useState(null);
  useEffect(() => {
    if (!isOwner || stream?.status !== "live") {
      setDetectVideoEl(null);
      return undefined;
    }
    let alive = true;
    const pickVideo = () => {
      if (!alive) return;
      // PREFER any <video> currently producing pixels (videoWidth > 0).
      // Without this preference we would latch on to the first
      // matching marker element even if it hasn't received frames yet,
      // and the hook would then read videoWidth=0 forever — silent
      // failure. The marker element on the same DOM tends to get
      // attached BEFORE LiveKit publishes its track, so scanning for
      // live pixels first is the safer default.
      const all = Array.from(document.querySelectorAll("video"));
      let el = all.find(
        (v) =>
          v.videoWidth > 0 &&
          v.videoHeight > 0 &&
          // Skip clearly remote viewer-side videos — they have a
          // different data attribute and aren't the seller's local
          // capture surface.
          v.getAttribute("data-stream-role") !== "viewer-remote" &&
          v.getAttribute("data-testid") !== "viewer-remote-video",
      );
      if (!el) {
        // No live pixels yet — fall back to whichever marker element
        // is on the page so the hook can re-read videoWidth as soon
        // as the camera starts producing.
        el =
          document.querySelector('video[data-stream-role="seller-local"]') ||
          document.querySelector('[data-testid="seller-local-video"]') ||
          document.querySelector('video[data-stream-video]') ||
          null;
      }
      // Only update state when the reference actually changes — avoids
      // re-running the hook's effect on every poll.
      setDetectVideoEl((prev) => (prev === el ? prev : el || null));
    };
    pickVideo();
    const t = setInterval(pickVideo, 500);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, [isOwner, stream?.status]);

  const autoDetect = useAutoDetect({
    streamId: stream?.id,
    videoEl: detectVideoEl,
    enabled:
      !!isOwner &&
      stream?.status === "live" &&
      autoDetectEnabled &&
      detectEligible && // hard yearly-Plus gate
      !autoDetectCandidate, // pause polling while a popup is on screen
    onCandidate: setAutoDetectCandidate,
    onError: ({ status, detail }) => {
      // Surface the FIRST failure so the seller knows why the popup
      // isn't appearing. Subsequent identical errors during this
      // session are squelched (the rail badge still goes amber).
      const key = `bidorah:detect:lastErrShown:${stream?.id || "x"}`;
      if (typeof window === "undefined" || sessionStorage.getItem(key) === detail) return;
      try { sessionStorage.setItem(key, detail); } catch (_) {}
      if (status === 402) {
        toast.error("Auto-Detect requires Bidorah Plus Yearly.");
      } else if (status === 429) {
        toast.warning("Slow down — too many detections. Pausing briefly.");
      } else if (status === 403) {
        toast.error("Only the stream owner can run Auto-Detect.");
      } else if (status === 503) {
        toast.error("Detector service isn't configured on this server.");
      } else {
        toast.warning(`Auto-Detect: ${detail || "trying again…"}`);
      }
    },
    // Browser-side diagnostics — fires once per session per code.
    // Covers the failure modes that previously left the rail silent:
    //  • draw-error  → canvas.drawImage threw (cross-origin tainting)
    //  • encode-error → canvas.toBlob threw
    //  • encode-null → toBlob called back null (tainted canvas)
    //  • no-frames   → 8 consecutive empty grabs (video element exists
    //                  but isn't producing pixels — usually a LiveKit
    //                  publishing race or wrong <video> picked).
    onDiag: ({ code, detail }) => {
      if (code === "draw-error" || code === "encode-error") {
        toast.error(`Auto-Detect blocked: ${detail || code}`);
      } else if (code === "encode-null") {
        toast.warning("Auto-Detect: video frame was empty — retrying.");
      } else if (code === "no-frames") {
        toast.warning(
          "Auto-Detect: waiting for camera frames — point the camera at an item.",
        );
      }
    },
    // First successful round-trip per session. The seller toggling
    // Detect ON wants instant feedback that the loop is actually
    // firing. Without this, a ~3 second silent gap before the first
    // GPT response looks like a dead button.
    onFirstSuccess: (d) => {
      const t = d?.is_sellable && d?.title
        ? `Detect armed — saw "${d.title}"`
        : "Detect armed — show an item to the camera.";
      toast.success(t, { duration: 3500 });
    },
  });

  // Single click handler shared by both rail circles. If the seller
  // qualifies (yearly Plus OR admin-granted) we toggle the local
  // detection loop. Otherwise we open the PlusUpgradeSheet locked to
  // the yearly plan so the upsell is unambiguous.
  const handleDetectRailTap = () => {
    if (detectEligible) {
      setAutoDetectEnabled((v) => {
        const next = !v;
        // Immediate user feedback so it never feels like a dead tap.
        // We don't gate this on the loop actually finding a video
        // element — the loop will fire a second toast (onDiag /
        // onFirstSuccess) once it knows.
        try {
          if (next) {
            // eslint-disable-next-line no-console
            console.info("[auto-detect] toggled ON", {
              streamId: stream?.id,
              streamStatus: stream?.status,
              isOwner,
              detectEligible,
              hasVideoEl: !!detectVideoEl,
              videoWidth: detectVideoEl?.videoWidth,
            });
            toast.success("Auto-Detect ON — scanning the camera…", {
              duration: 2400,
            });
            // Wipe the per-session error suppression key so the next
            // 502 / 503 / 429 surfaces fresh feedback instead of being
            // silently squelched from a prior toggle.
            try {
              sessionStorage.removeItem(`bidorah:detect:lastErrShown:${stream?.id || "x"}`);
            } catch (_) {}
          } else {
            toast.info("Auto-Detect paused.", { duration: 1800 });
          }
        } catch (_) {}
        return next;
      });
      return;
    }
    setPlusUpgradeReason(
      "Auto-Detect & List ships only on Bidorah Plus Yearly. Upgrade to let Bidoria spot every item on camera in 2 seconds.",
    );
    setPlusUpgradeOpen(true);
  };

  // Lock body scroll while on the live page — viewers must not be able to
  // accidentally scroll the video out of frame on mobile. Restored on unmount.
  useEffect(() => {
    const prevHtml = document.documentElement.style.overflow;
    const prevBody = document.body.style.overflow;
    const prevOverscroll = document.body.style.overscrollBehavior;
    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    document.body.style.overscrollBehavior = "none";
    return () => {
      document.documentElement.style.overflow = prevHtml;
      document.body.style.overflow = prevBody;
      document.body.style.overscrollBehavior = prevOverscroll;
    };
  }, []);

  const modDeleteMessage = async (m) => {
    if (!window.confirm(`Delete this message from ${m.user_name}?`)) return;
    try {
      await api.delete(`/mod/chat/${m.id}`);
      toast.success("Message deleted");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const modMuteUser = async (m) => {
    const mins = window.prompt(`Mute ${m.user_name} in this stream for how many minutes? (1-1440)`, "15");
    if (!mins) return;
    const duration = parseInt(mins, 10);
    if (Number.isNaN(duration) || duration < 1 || duration > 1440) {
      toast.error("Enter a number between 1 and 1440.");
      return;
    }
    const reason = window.prompt("Reason (optional)", "") || null;
    try {
      await api.post(`/mod/users/${m.user_id}/mute`, {
        stream_id: id,
        duration_minutes: duration,
        reason,
      });
      toast.success(`${m.user_name} muted for ${duration} min`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const modFlagStream = async () => {
    const category = window.prompt(
      "Flag category: policy / harassment / counterfeit / shill / scam / nsfw / other",
      "policy"
    );
    if (!category) return;
    const allowed = ["policy", "harassment", "counterfeit", "shill", "scam", "nsfw", "other"];
    if (!allowed.includes(category)) {
      toast.error("Invalid category.");
      return;
    }
    const reason = window.prompt("Describe what's wrong (5-500 chars)");
    if (!reason || reason.length < 5) {
      toast.error("A reason (5+ chars) is required.");
      return;
    }
    try {
      await api.post(`/mod/streams/${id}/flag`, { category, reason });
      toast.success("Flag raised. Admins notified.");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const inviteCohost = async () => {
    const email = window.prompt(
      "Invite a verified seller as co-host (must be in the same market segment):"
    );
    if (!email) return;
    try {
      await api.post(`/streams/${id}/invite-cohost`, { email: email.trim() });
      toast.success(`Invite sent to ${email.trim()} — they'll get a notification.`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const removeCohost = async () => {
    if (!window.confirm("Remove the co-host from this stream?")) return;
    try {
      await api.post(`/streams/${id}/leave-cohost`);
      toast.success("Co-host removed");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const acceptCohost = async () => {
    try {
      await api.post(`/streams/${id}/accept-cohost`);
      toast.success("You're now co-hosting — get on camera!");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const declineCohost = async () => {
    try {
      await api.post(`/streams/${id}/decline-cohost`);
      toast.info("Declined.");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const [gateError, setGateError] = useState(null);

  const load = async () => {
    try {
      const [s, c, g] = await Promise.all([
        api.get(`/streams/${id}`),
        api.get(`/streams/${id}/chat`),
        api.get(`/streams/${id}/giveaways/active`).catch(() => ({ data: null })),
      ]);
      setGateError(null);
      setStream(s.data);
      setLiveViewers(s.data.viewer_count || 0);
      setTapCount(s.data.tap_count || 0);
      const items = (c.data.items || []).map((m) => ({ kind: "chat", ...m }));
      setMessages(items);
      // Resurrect a pinned message if one exists in the loaded chat.
      const pinned = items.find((m) => m.pinned);
      if (pinned) setPinnedMessage(pinned);
      // Re-sync active giveaway so a freshly joined viewer doesn't miss it.
      if (g.data) setGiveaway(g.data);
      const firstActive = (s.data.listings || []).find(
        (x) => x.status === "active" && x.type === "auction"
      );
      setActiveListing(firstActive || (s.data.listings || [])[0] || null);
    } catch (err) {
      // Verification / auth gate from the backend comes through as a
      // structured 401/403 body. Capture it so we can render a clean
      // gate screen instead of a generic toast.
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 401 || status === 403) {
        const code = (detail && typeof detail === "object" && detail.code) || (status === 401 ? "auth_required" : "verification_required");
        setGateError({
          code,
          status,
          message:
            (detail && typeof detail === "object" && detail.message) ||
            (status === 401 ? "Sign in to enter a live show." : "Only verified accounts can enter live shows."),
          action_url:
            (detail && typeof detail === "object" && detail.action_url) ||
            (status === 401 ? "/login" : "/verify"),
          verification_status:
            detail && typeof detail === "object" && detail.verification_status,
        });
      } else {
        toast.error("Stream not found");
      }
    }
  };

  // Load stream-level moderators (so we can show the MOD badge on their
  // messages and so the appointed mod's UI flips into mod-mode automatically).
  const loadStreamMods = async () => {
    if (!user) return;
    try {
      const res = await api.get(`/streams/${id}/mods`);
      setStreamMods(res.data.items || []);
    } catch {
      // Silent — endpoint requires auth, guests just get empty.
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [id]);

  useEffect(() => {
    loadStreamMods();
    // eslint-disable-next-line
  }, [id, user?.user_id]);

  // WebSocket
  useEffect(() => {
    const wsUrl = SITE_ORIGIN.replace(/^http/, "ws") + `/api/ws/stream/${id}`;
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;
    ws.onopen = () => setWsConnected(true);
    ws.onclose = () => setWsConnected(false);
    ws.onmessage = (evt) => {
      try {
        const data = JSON.parse(evt.data);
        if (data.type === "welcome") {
          myConnIdRef.current = data.conn_id;
        } else if (data.type === "chat") {
          setMessages((prev) => [...prev, { kind: "chat", ...data.message }]);
        } else if (data.type === "chat_delete") {
          // Mark the matching chat message as deleted so it renders as removed.
          setMessages((prev) =>
            prev.map((m) =>
              m.kind === "chat" && m.id === data.message_id
                ? { ...m, is_deleted: true, deleted_by: data.by }
                : m
            )
          );
        } else if (data.type === "chat_muted_self") {
          toast.error(data.reason || "You are muted in this stream by a moderator.");
        } else if (data.type === "chat_mute") {
          toast.info(`Moderator muted ${data.user_id?.slice(-6)} until ${new Date(data.until).toLocaleTimeString()}`);
        } else if (data.type === "chat_unmute") {
          toast.success(`Moderator unmuted ${data.user_id?.slice(-6)}`);
        } else if (data.type === "chat_hide") {
          setMessages((prev) =>
            prev.map((m) =>
              m.kind === "chat" && m.id === data.message_id
                ? { ...m, is_hidden: true }
                : m
            )
          );
        } else if (data.type === "chat_unhide") {
          setMessages((prev) =>
            prev.map((m) =>
              m.kind === "chat" && m.id === data.message_id
                ? { ...m, is_hidden: false }
                : m
            )
          );
        } else if (data.type === "chat_pin") {
          setPinnedMessage(data.message);
          setMessages((prev) =>
            prev.map((m) =>
              m.kind === "chat" && m.id === data.message?.id
                ? { ...m, pinned: true }
                : { ...m, pinned: false }
            )
          );
        } else if (data.type === "chat_unpin") {
          setPinnedMessage(null);
          setMessages((prev) =>
            prev.map((m) => ({ ...m, pinned: false }))
          );
        } else if (data.type === "stream_mod_added" || data.type === "stream_mod_removed") {
          // Refresh the appointed-mods list so badges + permissions update live.
          loadStreamMods();
        } else if (data.type === "giveaway.started") {
          // Seller just launched a giveaway — pop the banner over the video.
          setGiveaway(data.giveaway);
          if (user && user.user_id !== data.giveaway?.seller_id) {
            toast("🎁 Giveaway started — tap to enter", { duration: 3500 });
          }
        } else if (data.type === "giveaway.entry") {
          // Live entry tick — bump the count without refetching.
          setGiveaway((prev) =>
            prev && prev.id === data.giveaway_id
              ? { ...prev, entries_count: data.entries_count }
              : prev
          );
        } else if (data.type === "giveaway.ended") {
          // Reveal winner; banner auto-clears after 10s via the useEffect.
          setGiveaway((prev) =>
            prev && prev.id === data.giveaway_id
              ? {
                  ...prev,
                  status: "ended",
                  winner_user_id: data.winner_user_id,
                  winner_name: data.winner_name,
                  winner_username: data.winner_username,
                }
              : prev
          );
          if (data.winner_username && user?.username === data.winner_username) {
            toast.success(`🏆 You won! ${data.prize}`, { duration: 8000 });
          }
        } else if (data.type === "tap") {
          // Bump the running counter on every tap. Double-taps count as 7
          // (matches the client-side heart burst + server `$inc` of 7).
          setTapCount((c) => c + (data.double ? 7 : 1));
          // Only feed the activity ticker on double-tap "likes" so it
          // doesn't blast on every single emoji ping.
          if (data.double && data.user_name) {
            pushActivity(`${data.user_name} liked`, "like");
          }
        } else if (data.type === "cohost_joined") {
          toast.success(`${data.name || "A seller"} joined as co-host`);
          load();
        } else if (data.type === "cohost_left") {
          toast.info("Co-host left the stream");
          load();
        } else if (data.type === "bid") {
          load();
          if (data.bidder_name && (data.amount || data.current_bid)) {
            const amt = data.amount || data.current_bid;
            pushActivity(`${data.bidder_name} bid $${Number(amt).toFixed(0)}`, "bid");
          } else {
            pushActivity("New bid placed", "bid");
          }
        } else if (data.type === "pin.added") {
          // New lot pinned to the stream — flip the active lot and reload
          // the listings panel so everyone sees the same item on stage.
          if (data.listing) {
            setActiveListing(data.listing);
            pushActivity(`On stage: ${data.listing.title}`, "join");
          }
          setLotHeat("cold");
          load();
        } else if (data.type === "pin.removed") {
          setActiveListing(null);
          setLotHeat("cold");
          load();
        } else if (data.type === "listings.imported") {
          // Seller imported existing shop products into the show — refresh
          // so the queue grows without anyone having to refresh the page.
          if (data.count) {
            pushActivity(`+${data.count} new item${data.count === 1 ? "" : "s"} in the shop`, "join");
          }
          load();
        } else if (data.type === "lot.heat") {
          // Server-driven heat tier change. Only apply when the event
          // is for the lot currently on stage — heat for an older
          // pinned lot is ignored so the badge doesn't flicker.
          const pinnedId = activeListing?.id || stream?.pinned_listing_id;
          if (!data.listing_id || data.listing_id === pinnedId) {
            setLotHeat(data.level || "cold");
            if (data.level === "frenzy" && data.prev_level !== "frenzy") {
              const now = Date.now();
              if (now - heatSoundRef.current > 8000) {
                heatSoundRef.current = now;
                pushActivity(`Frenzy! ${data.bids_5s || 15}+ bids/5s`, "bid");
              }
            }
          }
        } else if (data.type === "pin.timer.extended") {
          // Snipe protection — a bid in the final seconds bumped the
          // end time out. Update the active lot's timer locally so the
          // countdown on screen jumps to the new end-time immediately.
          if (data.listing_id && data.new_ends_at) {
            setActiveListing((prev) =>
              prev && prev.id === data.listing_id
                ? { ...prev, auction_ends_at: data.new_ends_at }
                : prev
            );
            toast.info("+10s — snipe protection");
          }
        } else if (data.type === "pin.hammered") {
          if (data.winner_name && data.winning_amount) {
            toast.success(`${data.winner_name} won for $${Number(data.winning_amount).toFixed(0)}`);
            pushActivity(
              `${data.winner_name} won $${Number(data.winning_amount).toFixed(0)}`,
              "bid"
            );
          }
          // Trigger the live sale platforms-style celebration overlay.  Self-win gets
          // the gold "YOU WON!" treatment; spectators get the calmer
          // "SOLD!" hero with the winner's name.
          const isSelf =
            (data.winner_id && user?.user_id === data.winner_id) ||
            (data.winner_username && user?.username === data.winner_username);
          setWinCelebration({
            key: `${data.listing_id || "lot"}-${Date.now()}`,
            amount: Number(data.winning_amount || 0),
            listingTitle: activeListing?.title || data.listing_title,
            image: activeListing?.images?.[0]
              ? fileUrl(activeListing.images[0])
              : null,
            winnerName: data.winner_username || data.winner_name,
            isSelf,
            cardLast4: isSelf ? defaultCard?.last4 : null,
          });
          setActiveListing(null);
          load();
        } else if (data.type === "tip" && data.tip) {
          setIncomingTip({ ...data.tip, _arrivedAt: Date.now() });
          // Also append a chat-style line so the chat feed shows the tip.
          setMessages((prev) => [
            ...prev,
            {
              kind: "tip",
              id: data.tip.id,
              user_name: data.tip.tipper_name,
              amount: data.tip.amount,
              message: data.tip.message,
              created_at: data.tip.created_at,
            },
          ]);
          pushActivity(
            `${data.tip.tipper_name} tipped $${Number(data.tip.amount).toFixed(0)}`,
            "tip"
          );
        } else if (data.type === "presence") {
          // Also merge into the feed so users can see who's entering/leaving.
          const entry = {
            kind: "presence",
            id: `pr_${data.ts}_${Math.random().toString(36).slice(2, 8)}`,
            event: data.event,
            user_name: data.user_name,
            is_admin: !!data.is_admin,
            viewer_count: data.viewer_count,
            created_at: data.ts,
          };
          setPresenceEvents((prev) => [...prev, entry].slice(-10));
          setMessages((prev) => [...prev, entry]);
          // Stash latest presence event so WatchingTab can patch its
          // viewer list locally — no extra API round-trip needed.
          setLastPresence(data);
          if (typeof data.viewer_count === "number") {
            setLiveViewers(data.viewer_count);
          }
          if (data.event === "join" && data.user_name) {
            // Carry the joiner's picture + display name so the join pill
            // can render their actual avatar (not just a first-letter
            // placeholder). Backend ws_presence broadcasts include
            // `picture` and `user_name`.
            pushActivity(`${data.user_name} joined`, "join", {
              picture: data.picture || data.user_picture || null,
              user_name: data.user_name,
            });
            // Keep the seller's persisted "Recent visitors" list fresh.
            refreshRecentJoiners();
          }
          // Admin entrance sparkle: any admin walking into the room fires a
          // golden burst visible to every viewer. Purely cosmetic — keeps
          // PII off-screen but adds a recognisable "the boss is here" cue.
          if (data.event === "join" && data.is_admin) {
            setAdminEntryTrigger({
              id: `adm_${data.ts || Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
              ts: data.ts,
            });
          }
        } else if (data.type === "kicked") {
          // The seller (or an admin) removed us from the stream.
          toast.error(
            data.reason ||
              "You were removed from this stream by the seller."
          );
          try {
            ws.close();
          } catch {}
          setTimeout(() => navigate("/live"), 1200);
        } else if (data.type === "stream_ended" || data.type === "stream_deleted") {
          // Hard shutdown. Close the WS so we stop receiving anything and
          // refresh the stream record — that flips status to "ended" and the
          // page swaps in the EndedStreamLockout view. For deletes the
          // stream record is gone so we navigate away.
          try { ws.close(); } catch {}
          if (data.type === "stream_deleted") {
            toast.info("This stream was removed by an admin.");
            setTimeout(() => navigate("/live"), 1200);
          } else {
            toast.info("This stream has ended.");
            load();
          }
        } else if (data.type === "stream.paused") {
          // Seller paused the show. Flip the stream status locally so the
          // paused overlay renders for every viewer without a full reload.
          setStream((prev) => (prev ? { ...prev, status: "paused" } : prev));
          toast.info("Seller paused the show. Hang tight — back in a moment.");
        } else if (data.type === "stream.resumed") {
          setStream((prev) => (prev ? { ...prev, status: "live" } : prev));
          toast.success("We're back live!");
        }
        // WebRTC signaling messages (webrtc_offer/answer/ice, request_stream) are
        // handled inside <StreamVideo/> via its own addEventListener('message').
      } catch {}
    };
    return () => ws.close();
  }, [id]);

  useEffect(() => {
    if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight;
  }, [messages]);


  const sendChat = async () => {
    const msg = chatInput.trim();
    if (!msg || !user) return;
    // Slash command: `/grade` opens the AI Condition Grader for the seller.
    if (msg.toLowerCase() === "/grade") {
      if (user.user_id === stream?.seller_id) {
        setGraderOpen(true);
      } else {
        toast.error("Only the seller can grade cards.");
      }
      setChatInput("");
      return;
    }
    // Clear the input optimistically so the user can keep typing while we
    // ship the message — same UX as platform/IG live chats.
    setChatInput("");
    const ws = wsRef.current;
    // Primary path: real-time WebSocket. Everyone in the room sees the
    // message instantly via the broadcast.
    if (ws && ws.readyState === 1) {
      try {
        ws.send(
          JSON.stringify({
            type: "chat",
            message: msg,
            token: "auto", // server reads cookie-based session
          })
        );
        return;
      } catch (_) {
        // ws.send can throw on a half-open socket — fall through to REST.
      }
    }
    // Fallback path: REST POST. On mobile networks the WS occasionally
    // drops (Capacitor WebView backgrounding, cellular hiccups) — without
    // this fallback the Send button silently does nothing.
    try {
      await api.post(`/streams/${id}/chat`, { message: msg });
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Couldn't send";
      toast.error(typeof detail === "string" ? detail : "Couldn't send message");
      // Restore so the user can retry without retyping.
      setChatInput(msg);
    }
  };

  /**
   * Insert `@username ` into the live chat input and focus the field.
   * Used by the click-on-name popover so a viewer can reply to someone
   * directly in chat without typing the handle by hand.
   */
  const replyToInChat = (handle) => {
    if (!handle) return;
    setChatInput((prev) => {
      const mention = `@${handle} `;
      // Don't double up if already mentioned at the start.
      if (prev.startsWith(mention)) return prev;
      return mention + prev;
    });
    // Focus whichever chat input is on screen (desktop or mobile).
    setTimeout(() => {
      const el =
        (chatInputRef.current && chatInputRef.current.offsetParent !== null && chatInputRef.current) ||
        (mobileChatInputRef.current && mobileChatInputRef.current.offsetParent !== null && mobileChatInputRef.current) ||
        chatInputRef.current ||
        mobileChatInputRef.current;
      if (el) el.focus();
    }, 30);
  };

  const placeBid = async () => {
    if (!activeListing) return;
    if (!user) return navigate("/login");
    const amount = parseFloat(bidInput);
    if (!amount) return;
    try {
      await api.post(`/listings/${activeListing.id}/bid`, { amount });
      toast.success(`Bid: $${amount.toFixed(2)}`);
      setBidInput("");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const startNow = async () => {
    try {
      await api.post(`/streams/${id}/start`);
      toast.success("You're live! Bookmarkers were notified.");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const buyNow = async () => {
    if (!activeListing) return;
    if (!user) return navigate("/login");
    const price = Number(activeListing.buy_now_price || 0);
    const card = defaultCard;
    const confirmText = card
      ? `Pay $${price.toFixed(2)} with ${card.brand.toUpperCase()} •••• ${card.last4}?`
      : `Buy ${activeListing.title} for $${price.toFixed(2)} right now?`;
    if (!window.confirm(confirmText)) return;
    try {
      const r = await api.post(`/streams/${id}/buy-now/${activeListing.id}`);
      const data = r.data || {};

      // Path A — one-tap charge succeeded server-side.
      if (data.paid && data.order_id) {
        toast.success("Paid! Heading to your order.");
        setTimeout(() => navigate(`/order/${data.order_id}`), 600);
        return;
      }

      // Path B — 3DS / SCA challenge. Let Stripe.js run the bank's modal,
      // then poll the instant-checkout status endpoint to finalize.
      if (data.requires_action && data.client_secret && data.pi_id) {
        toast("Card needs a quick verification…", { duration: 2500 });
        const { loadStripe } = await import("@stripe/stripe-js");
        const stripe = await loadStripe(data.publishable_key);
        if (!stripe) {
          toast.error("Could not load Stripe — continue from the order page.");
          if (data.order_id) navigate(`/order/${data.order_id}`);
          return;
        }
        const { error: scaErr } = await stripe.confirmCardPayment(data.client_secret);
        if (scaErr) {
          toast.error(scaErr.message || "Card verification failed.");
          if (data.order_id) navigate(`/order/${data.order_id}`);
          return;
        }
        try {
          const s = await api.get(`/payments/instant-checkout/status/${data.pi_id}`);
          if (s.data.status === "succeeded") {
            toast.success("Paid! Heading to your order.");
          } else {
            toast(`Payment status: ${s.data.status}. Open your order to retry.`);
          }
        } catch {
          /* ignore — fall through to order page */
        }
        if (data.order_id) setTimeout(() => navigate(`/order/${data.order_id}`), 500);
        return;
      }

      // Path C — saved-card charge failed (e.g. card_declined). Order
      // still exists in pending_payment; nudge the buyer to the order
      // page to pay another way.
      if (data.paid === false && data.error) {
        toast.error(`Saved card declined. Choose another card.`);
        if (data.order_id) navigate(`/order/${data.order_id}`);
        return;
      }

      // Path D — no saved card (classic flow). Redirect to /order to
      // complete checkout there.
      if (data.order_id) {
        toast.success("Locked in! Redirecting to checkout…");
        setTimeout(() => navigate(`/order/${data.order_id}`), 900);
      }
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const [endStreamConfirmOpen, setEndStreamConfirmOpen] = useState(false);

  // Android hardware back button — per user spec:
  //   • Seller of THIS stream → trigger the "End the show?" confirmation
  //     AlertDialog (same flow as the on-screen End-stream pill).
  //   • Viewer / buyer → land them on "/", not the prior history page,
  //     so the back gesture always feels like "leave the live show".
  // Uses Capacitor App.backButton (native) and falls back gracefully on
  // web where the browser owns navigation.
  useEffect(() => {
    if (!stream) return undefined;
    let cleanup = () => {};
    (async () => {
      try {
        const { App: CapApp } = await import("@capacitor/app");
        if (!CapApp?.addListener) return;
        const isSeller = user && user.user_id === stream.seller_id;
        const handle = await CapApp.addListener("backButton", () => {
          if (isSeller && stream.status === "live") {
            setEndStreamConfirmOpen(true);
          } else {
            navigate("/");
          }
        });
        cleanup = () => { try { handle.remove(); } catch { /* noop */ } };
      } catch { /* @capacitor/app not present on web */ }
    })();
    return () => cleanup();
  }, [stream, user, navigate]);
  const [endingStream, setEndingStream] = useState(false);
  const endStream = () => {
    // Open the styled confirmation dialog. The actual /streams/{id}/end
    // POST happens in `confirmEndStream` once the seller hits CONFIRM.
    setEndStreamConfirmOpen(true);
  };
  const confirmEndStream = async () => {
    setEndingStream(true);
    try {
      const r = await api.post(`/streams/${id}/end`);
      toast.success("Stream ended.");
      if (r.data?.summary) setRecap(r.data.summary);
      setEndStreamConfirmOpen(false);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setEndingStream(false);
    }
  };

  // Pause / resume the live show. Used by the Android hardware back button
  // and the dedicated PAUSE/RESUME pill in the seller toolbar. Pausing
  // keeps the stream open (viewers see a "paused" overlay) but blocks
  // bids and hides the show from the public discovery feed until the
  // seller resumes.
  const pauseStream = useCallback(async () => {
    try {
      await api.post(`/streams/${id}/pause`);
      setStream((prev) => (prev ? { ...prev, status: "paused" } : prev));
      toast.success("Show paused. Viewers can wait — tap Resume when you're back.");
    } catch (e) {
      toast.error(apiError(e));
    }
  }, [id]);

  const resumeStream = useCallback(async () => {
    try {
      await api.post(`/streams/${id}/resume`);
      setStream((prev) => (prev ? { ...prev, status: "live" } : prev));
      toast.success("You're live again.");
    } catch (e) {
      toast.error(apiError(e));
    }
  }, [id]);

  // Intercept the Android hardware back button for the SELLER of a live or
  // paused show. Instead of popping the WebView stack (which would drop the
  // seller out of their own stream with no warning), ask whether they want
  // to pause the show. Non-sellers get the default back behaviour.
  // Intercept the Android hardware back button on the live-stream page.
  //
  //   • SELLER (or co-host) → "Pause your live show?" prompt, then pause.
  //     Leaving accidentally while streaming is catastrophic, so we guard.
  //
  //   • VIEWER → navigate back to `/live` (the live-show discovery grid)
  //     instead of popping WebView history. Tapping a live show from a
  //     push notification or deep-link leaves an empty history; default
  //     back would quit the app. Routing to `/live` keeps the viewer in
  //     the "live shows" context for a continuous browse experience.
  useEffect(() => {
    if (!stream) return undefined;
    const meIsSellerHere =
      !!(user && (user.user_id === stream.seller_id ||
        (stream.cohost_ids || []).includes(user.user_id)));
    const isLiveOrPaused = stream.status === "live" || stream.status === "paused";
    if (meIsSellerHere && !isLiveOrPaused) return undefined;

    let handle = null;
    let cancelled = false;
    (async () => {
      try {
        const { App } = await import("@capacitor/app");
        handle = await App.addListener("backButton", () => {
          if (cancelled) return;
          if (meIsSellerHere) {
            if (stream.status === "paused") {
              const go = window.confirm(
                "Your show is paused. Leave the page anyway?\n\n" +
                "Tap Cancel to stay and resume when you're ready."
              );
              if (go) navigate("/dashboard");
              return;
            }
            const ok = window.confirm(
              "Pause your live show?\n\n" +
              "Tap OK to pause — viewers stay in the room with a 'Paused' " +
              "overlay until you resume. Tap Cancel to keep streaming."
            );
            if (ok) pauseStream();
            return;
          }
          // Viewer: always land on the live discovery grid.
          navigate("/live");
        });
      } catch (_) {
        // Not running inside Capacitor (web browser) — noop.
      }
    })();
    return () => {
      cancelled = true;
      try {
        if (handle && typeof handle.remove === "function") handle.remove();
      } catch (_) {}
    };
  }, [user, stream, navigate, pauseStream]);

  // Live auction countdown — drives the FirePill animation on the BID
  // button. Ticks every 250ms so the explosion lands precisely on the
  // 0-second transition. Recomputes whenever auction_ends_at changes
  // (e.g., a last-second bid extending the timer).
  // (Declared BEFORE the `if (!stream)` early return so React's
  // rules-of-hooks invariant holds.)
  const [bidSecondsLeft, setBidSecondsLeft] = useState(null);
  // Sound for the FirePill — opt-in. Browsers gate audio behind a user
  // gesture and unexpected BOOMs would be a worse experience than no
  // sound, so we default to muted and persist the user's choice.
  const [bidSoundOn, setBidSoundOn] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage?.getItem("bidora.bid_sound_on") === "1";
  });
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage?.setItem("bidora.bid_sound_on", bidSoundOn ? "1" : "0");
  }, [bidSoundOn]);
  useEffect(() => {
    const endsAt = activeListing?.auction_ends_at;
    if (!endsAt || activeListing?.status !== "active" || activeListing?.type !== "auction") {
      setBidSecondsLeft(null);
      return undefined;
    }
    const endMs = new Date(endsAt).getTime();
    const compute = () => {
      const ms = endMs - Date.now();
      setBidSecondsLeft(ms <= 0 ? 0 : Math.ceil(ms / 1000));
    };
    compute();
    const id = setInterval(compute, 250);
    return () => clearInterval(id);
  }, [activeListing?.auction_ends_at, activeListing?.status, activeListing?.type]);

  // Access gate (verification / auth required). Backend returns
  // structured 401/403; we render a clear lockout screen with a CTA
  // into the right flow (sign-in or ID verification).
  if (gateError) {
    return <LiveStreamAccessGate gate={gateError} />;
  }

  if (!stream) return (
    <div className="min-h-screen"><Navbar /><div className="max-w-7xl mx-auto p-10">Loading stream…</div></div>
  );

  // Hard shutdown: once a seller (or admin) has ended the stream, NO ONE can
  // re-enter — not even the seller. The live experience is sealed and the
  // page renders a friendly "stream has ended" lockout. The seller can still
  // get to their post-show summary via /dashboard.
  if (stream.status === "ended") {
    return <EndedStreamLockout stream={stream} />;
  }

  // Age gate — Whatnot-style. When the stream is `age_restricted` the
  // viewer must confirm they're 18+ before the live UI mounts. We bypass
  // the gate for the seller themselves (they obviously made the call)
  // and for admins (moderation). Confirmation is stored per-user +
  // per-stream in localStorage so re-entering doesn't re-prompt.
  if (
    stream.age_restricted &&
    user &&
    user.user_id !== stream.seller_id &&
    user.role !== "admin"
  ) {
    const ackKey = `bidora.age18.${user.user_id}.${stream.id}`;
    if (typeof window !== "undefined" && !window.localStorage.getItem(ackKey)) {
      return (
        <AgeGate18
          stream={stream}
          onConfirm={() => {
            window.localStorage.setItem(ackKey, "1");
            // Re-render: a fresh state ping forces React to re-evaluate.
            setStream({ ...stream });
          }}
        />
      );
    }
  }

  const minBid = activeListing
    ? (activeListing.current_bid || activeListing.starting_bid || 0) +
      (activeListing.highest_bidder_id ? 1 : 0)
    : 0;

  return (
    <div
      className="fixed inset-0 bg-zinc-50 flex flex-col overflow-hidden"
      data-testid={`live-stream-${stream.id}`}
      style={{ height: "100dvh" }}
    >
      <SeoHead
        title={`${stream.title || `${stream.seller_name} live`} on Bidorah`}
        description={`${stream.status === "live" ? "🔴 LIVE now" : "Live auction"} — ${stream.seller_name} is hosting a verified live auction on Bidorah. Bid live, win real.`}
        canonical={`https://bidorah.com/stream/${stream.id}`}
        type="video.other"
        jsonLd={[
          liveStreamLd(stream),
          breadcrumbLd([
            { name: "Home", url: "https://bidorah.com/" },
            { name: "Live shows", url: "https://bidorah.com/live" },
            { name: stream.seller_name || "Stream", url: `https://bidorah.com/stream/${stream.id}` },
          ]),
        ]}
      />
      {/* Navbar deliberately hidden on both mobile AND desktop. The live
          show is full-bleed by design — the noisy site-wide nav (Bidoria
          NEW / Prices / Plus / Why us / Sell on Bidorah) competes with
          the stream content. To exit, viewers use the X breadcrumb or
          browser back. Matches the APK behavior. */}
      <TipToastQueue incoming={incomingTip} />
      <main
        className={`flex-1 grid grid-cols-1 gap-0 bg-zinc-50 transition-[grid-template-columns] duration-300 ${
          immersive ? "md:grid-cols-1" : "md:grid-cols-[300px_minmax(0,1fr)_340px]"
        }`}
        onTouchStart={onSwipeStart}
        onTouchEnd={onSwipeEnd}
        data-immersive={immersive ? "true" : "false"}
        data-testid="stream-main-grid"
      >
        {/* Immersive-mode hint chip — appears briefly when the viewer
            swipes left to hide the comments + rail. Auto-dismisses
            after 3.5s so a power user sees it once then never again. */}
        {showImmersiveHint && (
          <div
            className="fixed top-4 left-1/2 -translate-x-1/2 z-[60] pointer-events-none animate-in fade-in slide-in-from-top-2"
            data-testid="immersive-hint"
          >
            <div className="rounded-full bg-black/70 backdrop-blur-md text-white text-xs font-semibold tracking-wide px-4 py-2 shadow-lg border border-white/10">
              Swipe right to bring chat back
            </div>
          </div>
        )}
        {/* ---------- LEFT: Shop panel ---------- */}
        <aside
          className={`flex-col border-r border-zinc-200 bg-white overflow-y-auto max-h-screen ${
            immersive ? "hidden" : "hidden md:flex"
          }`}
          data-testid="stream-shop-rail"
        >
          <div className="px-5 pt-5 pb-3 sticky top-0 bg-white z-10 border-b border-zinc-100">
            <div className="flex items-center justify-between">
              <div className="font-display font-black text-xl text-zinc-900 tracking-tight">Shop</div>
              {user && user.user_id === stream.seller_id && (
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => setShowImportShop(true)}
                    className="h-8 px-3 rounded-full bg-white border border-zinc-300 text-zinc-800 text-xs font-bold hover:border-[#1D78AB] hover:text-[#1D78AB] flex items-center gap-1"
                    data-testid="desktop-shop-import-btn"
                    title="Import existing shop listings into this live show"
                  >
                    <DownloadSimpleIcon size={12} weight="bold" />
                    Import
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowQuickAdd(true)}
                    className="h-8 px-3 rounded-full bg-[#1D78AB] text-white text-xs font-bold hover:bg-[#165d85] flex items-center gap-1"
                    data-testid="desktop-shop-add-item"
                  >
                    <span className="text-base leading-none">+</span> Add item
                  </button>
                </div>
              )}
            </div>
            <div className="mt-3 relative">
              <Input
                placeholder="Search shop…"
                className="rounded-full bg-zinc-100 border-zinc-200 pl-10 h-10 text-sm"
                data-testid="shop-search-input"
              />
              <MagnifyingGlassIcon size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-400" />
            </div>
            {/* Filter chips — single horizontal row, no wrap.  Padding +
                gap tuned so all 5 (Filter · Sort · Auction · Buy Now ·
                Sold) fit visibly inside the 300px sidebar without
                truncation. Each chip auto-sizes to its label. */}
            <div className="flex flex-nowrap gap-1 mt-3">
              {[
                { label: "Filter", id: "filter" },
                { label: "Sort", id: "sort" },
                { label: "Auction", id: "auction" },
                { label: "Buy Now", id: "buy-now" },
                { label: "Sold", id: "sold" },
              ].map((t) => (
                <button
                  key={t.id}
                  className="shrink-0 whitespace-nowrap px-2 py-1 rounded-full border border-zinc-200 text-[11px] font-semibold text-zinc-700 hover:border-[#1D78AB] hover:text-[#1D78AB] bg-white"
                  data-testid={`shop-filter-${t.id}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
          <div className="px-5 py-4">
            <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-semibold mb-2">
              Products ({stream.listings?.filter((l) => l.type !== "giveaway").length || 0})
            </div>
            <div className="space-y-3">
              {(stream.listings || []).filter((l) => l.type !== "giveaway").map((l) => {
                const sold = l.status === "sold";
                const isActive = activeListing?.id === l.id;
                return (
                  <button
                    key={l.id}
                    onClick={() => setActiveListing(l)}
                    className={`w-full rounded-2xl border p-3 text-left transition ${
                      isActive
                        ? "border-[#1D78AB] bg-[#1D78AB]/5"
                        : "border-zinc-200 hover:border-zinc-300 bg-white"
                    }`}
                    data-testid={`shop-product-${l.id}`}
                  >
                    <div className="flex gap-3">
                      <div className="h-14 w-14 rounded-xl bg-zinc-100 flex-shrink-0 overflow-hidden">
                        {l.images?.[0] && (
                          <img src={fileUrl(l.images[0])} className="h-full w-full object-cover" alt="" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-zinc-900 truncate">{l.title}</div>
                        <div className="text-xs text-zinc-500 mt-0.5">
                          <span className="font-bold text-zinc-900">${(l.current_bid || l.price || 0).toFixed(0)}</span>
                          <span className="mx-1.5">·</span>
                          {l.bid_count || 0} bid{(l.bid_count || 0) === 1 ? "" : "s"}
                        </div>
                      </div>
                    </div>
                    <div
                      className={`mt-2 w-full text-center text-xs font-semibold py-2 rounded-full ${
                        sold
                          ? "bg-red-100 text-red-700"
                          : "bg-zinc-100 text-zinc-700"
                      }`}
                    >
                      {sold ? "Sold" : l.type === "auction" ? "Pre-bid" : "Buy"}
                    </div>
                  </button>
                );
              })}
            </div>
            {stream.listings?.some((l) => l.type === "giveaway") && (
              <>
                <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-semibold mt-6 mb-2">
                  Upcoming Giveaways ({stream.listings.filter((l) => l.type === "giveaway").length})
                </div>
                <div className="space-y-3">
                  {stream.listings.filter((l) => l.type === "giveaway").map((l) => (
                    <div
                      key={l.id}
                      className="rounded-2xl border border-zinc-200 bg-white p-3 flex gap-3"
                      data-testid={`shop-giveaway-${l.id}`}
                    >
                      <div className="h-14 w-14 rounded-xl bg-zinc-100 flex-shrink-0 overflow-hidden">
                        {l.images?.[0] && (
                          <img src={fileUrl(l.images[0])} className="h-full w-full object-cover" alt="" />
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-semibold text-zinc-900 truncate">{l.title}</div>
                        <div className="text-xs text-zinc-500 mt-0.5">Qty. {l.quantity || 1}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </aside>

        {/* ---------- CENTER: Video stage ---------- */}
        <section
          className="relative bg-black lg:bg-zinc-900 flex flex-col"
          data-testid="stream-stage"
        >
          <div className="relative flex-1 flex items-stretch lg:items-center justify-center px-0 lg:px-6 py-0 lg:py-4">
            <div className="relative w-full h-[100dvh] md:h-auto md:max-w-[500px] md:aspect-[9/16] bg-black rounded-none md:rounded-2xl overflow-hidden shadow-2xl">
              <CinemaStage
                stream={stream}
                user={user}
                wsRef={wsRef}
                myConnIdRef={myConnIdRef}
                setVideoBackend={setVideoBackend}
                setCameraOn={setCameraOn}
              />

              {/* Native-only dark gradients at the top + bottom of the video.
                  On some Samsung / Xiaomi Android builds, env(safe-area-inset-*)
                  reports 0 even when the OS status / nav bars are drawn over
                  the WebView — meaning the white OS icons sit on top of light
                  parts of the video and become invisible. These gradients
                  guarantee a dark strip behind the OS chrome regardless of
                  what's playing on the video. Pointer-events-none so they
                  never intercept taps. md:hidden so they don't show on the
                  desktop boxed layout. */}
              <div
                aria-hidden
                className="md:hidden absolute inset-x-0 top-0 z-10 pointer-events-none"
                style={{
                  height: "calc(max(var(--sai-top, env(safe-area-inset-top, 0px)), 28px) + 64px)",
                  // Solid black at the very top (where the OS clock/battery sit) so
                  // those icons read as either black-on-black or white-on-black —
                  // never washed-out against a light video frame. Fades to clear
                  // below so our seller card/End-stream pill float over the video.
                  background:
                    "linear-gradient(to bottom, #000 0%, rgba(0,0,0,0.92) 30%, rgba(0,0,0,0.55) 70%, rgba(0,0,0,0) 100%)",
                }}
                data-testid="stream-top-scrim"
              />
              <div
                aria-hidden
                className="md:hidden absolute inset-x-0 bottom-0 z-10 pointer-events-none"
                style={{
                  height: "calc(max(env(safe-area-inset-bottom, 0px), 48px) + 120px)",
                  background:
                    "linear-gradient(to top, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.45) 60%, rgba(0,0,0,0) 100%)",
                }}
                data-testid="stream-bottom-scrim"
              />

              {/* Founder head-start gate — covers the video for non-founders
                  during the first 30s the stream is live. */}
              {stream.founder_locked && (
                <FounderHeadStartOverlay
                  seconds={stream.seconds_until_public}
                  onUnlock={load}
                />
              )}

              {/* Live giveaway banner — floats top-center over the video.
                  Renders ACTIVE (countdown + Join button) or ENDED (winner
                  reveal). Auto-dismisses 10s after end. */}
              <AnimatePresence>
                {giveaway && (
                  <GiveawayBanner
                    giveaway={giveaway}
                    currentUser={user}
                    isHost={!!(user && user.user_id === stream.seller_id)}
                    onUpdate={(g) => setGiveaway(g)}
                  />
                )}
              </AnimatePresence>

              {/* Auto-detect popup — seller sees this when Bidoria
                  recognises a sellable item in the camera frame. */}
              {isOwner && autoDetectCandidate && (
                <AutoDetectPopup
                  candidate={autoDetectCandidate}
                  streamId={stream.id}
                  onDismiss={(sig) => {
                    autoDetect.mute(sig);
                    setAutoDetectCandidate(null);
                  }}
                  onListed={() => {
                    setAutoDetectCandidate(null);
                    load();
                  }}
                  onEdit={(prefill) => {
                    setQuickAddPrefill(prefill);
                    setShowQuickAdd(true);
                    setAutoDetectCandidate(null);
                  }}
                />
              )}

              {/* Tap-to-react overlay (live-shopping style). Hidden for the
                  seller until their camera is actually on so the giant "Tap
                  to go live" button isn't blocked by this overlay's pointer
                  events. Viewers always see it once the stream is live. */}
              {stream.status === "live" && (() => {
                const meIsSeller = !!(user && (user.user_id === stream.seller_id || (stream.cohost_ids || []).includes(user.user_id)));
                if (meIsSeller && !cameraOn) return null;
                return (
                  <LiveTapLayer
                    wsRef={wsRef}
                    myConnIdRef={myConnIdRef}
                    tapCount={tapCount}
                  />
                );
              })()}

              {/* Bidoria audio player — every viewer auto-plays incoming
                  cohost MP3 chunks. Hidden audio element. */}
              <AICoHostPlayer wsRef={wsRef} onLine={setLastAILine} />

              {/* Admin entry sparkle — fires whenever any admin joins the
                  room. Visible to every viewer in the show. */}
              <AdminEntrySparkle trigger={adminEntryTrigger} />

              {/* Bidoria control panel — visible to: stream's seller, any
                  human cohost, AND ANY ADMIN (so admins can demo / control
                  Bidoria on every live show, not just streams they own). */}
              {user && (
                user.user_id === stream.seller_id
                || (stream.cohost_ids || []).includes(user.user_id)
                || (user.role || "").toLowerCase() === "admin"
              ) && (
                <AICoHostPanel streamId={stream.id} lastLine={lastAILine} />
              )}

              {/* Seller card — top-left. Always visible (including for the
                  streamer themself) so they can see their own avatar + LIVE
                  pill while broadcasting. Pushed well below the iOS status
                  bar (battery/wifi) on mobile via a larger safe-area offset. */}
              <div
                className="absolute left-3 right-20 z-20 flex flex-col gap-1.5"
                style={{ top: "calc(max(var(--sai-top, env(safe-area-inset-top, 0px)), 28px) + 12px)" }}
                data-testid="seller-card"
              >
                <div className="flex items-center gap-2">
                  <SellerAvatar
                    name={stream.seller_name}
                    picture={stream.seller_picture}
                    size={44}
                    editable={false}
                    sparkle={!!stream.seller_avatar_sparkle}
                  />
                  <div className="min-w-0 flex-1">
                    <Link
                      to={`/seller/${stream.seller_id}`}
                      className="text-white font-semibold text-sm truncate hover:underline drop-shadow inline-flex items-center gap-1"
                      data-testid="stream-seller-link"
                    >
                      {stream.seller_name}
                      <VerifiedBadge visible={stream.seller_verified_badge} size={14} />
                    </Link>
                    <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                      {/* MOBILE-ONLY: LIVE viewer-count pill, sits right next
                          to the seller name. Desktop keeps its top-right pill
                          (ViewerListPopover) untouched. */}
                      <button
                        type="button"
                        onClick={() => {
                          // Open the chat drawer pre-pinned to the
                          // Watching tab so sellers can see who's in the
                          // room. Buyers also get the list; same lift.
                          setRightTab("watching");
                          setMobileChatOpen(true);
                        }}
                        className="md:hidden inline-flex items-center gap-1 rounded-full bg-red-600 text-white text-[10px] font-bold px-2 py-0.5 uppercase tracking-wider shadow hover:bg-red-500 active:bg-red-700"
                        data-testid="mobile-live-viewer-pill"
                        title="See who's watching"
                      >
                        <span className="h-1.5 w-1.5 rounded-full bg-red-300 animate-pulse shadow-[0_0_6px_rgba(252,165,165,0.9)]" />
                        LIVE
                        <span className="ml-0.5 inline-flex items-center gap-0.5">
                          <UsersThreeIcon size={11} weight="fill" /> {liveViewers}
                        </span>
                      </button>
                      {stream.seller_trust_tier && (
                        <span className="pill pill-dark text-[10px]">
                          <StarIcon size={10} weight="fill" className="text-amber-400" />
                          {stream.seller_rating?.toFixed?.(1) || "5.0"}
                        </span>
                      )}
                      <FollowButton sellerId={stream.seller_id} size="xs" />
                    </div>
                  </div>
                </div>

                {/* Co-host row — only renders when a co-host has joined */}
                {(stream.cohosts || []).map((c) => (
                  <div
                    key={c.user_id}
                    className="flex items-center gap-2 bg-black/40 backdrop-blur rounded-full pr-3 pl-1 py-1 self-start"
                    data-testid={`cohost-card-${c.user_id}`}
                  >
                    <SellerAvatar name={c.name || "Co-host"} picture={c.picture} size={28} />
                    <div className="text-white text-xs">
                      <span className="font-semibold">{c.name}</span>
                      <span className="ml-1.5 pill bg-[#1D78AB] text-white text-[9px] uppercase tracking-widest px-1.5 py-0.5">
                        CO-HOST
                      </span>
                    </div>
                  </div>
                ))}

                {/* Recent visitors — seller only. Tappable pill that opens
                    a small popover listing the last 5 unique users who
                    joined this stream (deduped, persisted on the stream
                    doc so it survives page refresh). Helps sellers spot
                    regulars and call them out by name. */}
                {user?.user_id === stream.seller_id && recentJoiners.length > 0 && (
                  <div className="self-start mt-1 relative">
                    <Button
                      size="sm"
                      variant="outline"
                      className="bg-black/40 backdrop-blur border-white/20 text-white hover:bg-black/60 h-8 px-3 text-[11px] font-bold rounded-full"
                      onClick={() => setShowRecentJoiners((v) => !v)}
                      data-testid="recent-visitors-btn"
                    >
                      👥 Recent · {recentJoiners.length}
                    </Button>
                    {showRecentJoiners && (
                      <div
                        className="absolute z-30 left-0 mt-2 w-64 rounded-xl bg-zinc-900/95 backdrop-blur border border-white/10 shadow-2xl p-3 space-y-2"
                        data-testid="recent-visitors-popover"
                      >
                        <div className="text-[10px] uppercase tracking-wide text-white/60 font-bold">
                          Last 5 unique visitors
                        </div>
                        {recentJoiners.map((j) => (
                          <div
                            key={j.user_id}
                            className="flex items-center gap-2"
                            data-testid={`recent-visitor-${j.user_id}`}
                          >
                            <SellerAvatar
                              name={j.user_name || "Guest"}
                              picture={j.picture}
                              size={28}
                              className="shrink-0"
                            />
                            <div className="min-w-0 flex-1">
                              <div className="text-sm text-white font-semibold truncate">
                                {j.user_name}
                              </div>
                              <div className="text-[10px] text-white/50">
                                {new Date(j.joined_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Primary host: invite or remove cohost */}
                {/* Invite co-host — desktop ONLY in the seller card. Mobile
                    exposes the same action as a Cohost RailButton on the
                    right rail to keep this top-left card uncluttered. */}
                {user?.user_id === stream.seller_id && stream.status === "live" && (
                  <div className="self-start mt-1 hidden md:flex">
                    {(stream.cohosts || []).length === 0 ? (
                      <Button
                        size="sm"
                        variant="outline"
                        className="bg-black/40 backdrop-blur border-white/20 text-white hover:bg-black/60 h-8 px-3 text-[11px] font-bold rounded-full"
                        onClick={inviteCohost}
                        data-testid="invite-cohost-btn"
                      >
                        + Invite co-host
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        className="bg-black/40 backdrop-blur border-white/20 text-white hover:bg-red-600 h-8 px-3 text-[11px] font-bold rounded-full"
                        onClick={removeCohost}
                        data-testid="remove-cohost-btn"
                      >
                        ✕ Remove co-host
                      </Button>
                    )}
                  </div>
                )}

                {/* Invitee banner: pending invite for this user */}
                {user && stream.cohost_invite?.status === "pending" &&
                  stream.cohost_invite?.invitee_user_id === user.user_id && (
                    <div className="self-start mt-1 flex items-center gap-2 bg-amber-500 text-black rounded-full pr-2 pl-3 py-1 shadow-lg" data-testid="cohost-invite-banner">
                      <span className="text-xs font-bold uppercase tracking-wider">
                        You're invited to co-host
                      </span>
                      <Button
                        size="sm"
                        className="h-7 px-3 text-[11px] font-bold rounded-full bg-black text-white hover:bg-zinc-800"
                        onClick={acceptCohost}
                        data-testid="accept-cohost-btn"
                      >
                        Accept
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 px-2 text-[11px] font-bold rounded-full border-black/20 hover:bg-black/10"
                        onClick={declineCohost}
                        data-testid="decline-cohost-btn"
                      >
                        Decline
                      </Button>
                    </div>
                  )}
              </div>

              {/* Recent joins — mobile only. Floats at the LEFT CENTER of the
                  video so entering users slide into view in the middle-left
                  band. Stack grows upward: newest join sits lowest (closest
                  to eye line), older ones drift up and fade via the
                  activityFeed TTL. */}
              {activityFeed.filter((e) => e.kind === "join").length > 0 && (
                <div
                  className="md:hidden absolute left-3 right-20 z-10 pointer-events-none flex flex-col-reverse gap-1.5"
                  style={{ bottom: "calc(max(env(safe-area-inset-bottom, 0px), 16px) + 180px)" }}
                  data-testid="recent-joins-strip"
                >
                  {activityFeed.filter((e) => e.kind === "join").slice(-3).map((evt) => (
                    <div
                      key={evt.id}
                      className="inline-flex items-center gap-2 max-w-fit bg-black/45 backdrop-blur-sm rounded-full pr-3 pl-1 py-1 text-white text-xs font-semibold shadow-lg animate-join-slide-in"
                      data-testid={`join-pill-${evt.id}`}
                    >
                      <SellerAvatar
                        name={evt.user_name || (evt.text || "").replace(/ joined$/i, "") || "Guest"}
                        picture={evt.picture}
                        size={24}
                        className="shrink-0"
                      />
                      <span className="truncate max-w-[180px]">{evt.text}</span>
                    </div>
                  ))}
                </div>
              )}
              <div className="hidden md:block">
                <ViewerListPopover
                  streamId={id}
                  liveCount={liveViewers}
                  enabled={!!(user && stream && (user.user_id === stream.seller_id || user.role === "admin"))}
                  refreshKey={presenceEvents.length}
                />
              </div>

              {/* Tip — the visible mobile pill has been removed per
                  feedback (the Tip action is already a tile in the
                  "More" sheet, so showing it twice was redundant). We
                  still render TipSheet here with a hidden trigger so the
                  More-sheet tile and the desktop right-rail button can
                  both open the same dialog via `openOverride`. */}
              {stream.status === "live" && !(user && user.user_id === stream.seller_id) && (
                <div className="md:hidden">
                  <TipSheet
                    streamId={stream.id}
                    sellerName={stream.seller_name}
                    user={user}
                    openOverride={tipSheetOpen}
                    onOpenOverrideChange={setTipSheetOpen}
                    hideTrigger
                  />
                </div>
              )}
              {/* Hidden TipSheet that the desktop right-rail Tip button and
                  the mobile More-sheet Tip tile both open via the controlled
                  `openOverride` prop. Renders the dialog without a visible
                  trigger so the right rail stays the single PC entry point. */}
              {stream.status === "live" && !(user && user.user_id === stream.seller_id) && (
                <div className="hidden md:block">
                  <TipSheet
                    streamId={stream.id}
                    sellerName={stream.seller_name}
                    user={user}
                    openOverride={tipSheetOpen}
                    onOpenOverrideChange={setTipSheetOpen}
                    hideTrigger
                  />
                </div>
              )}
              {/* Hidden TipSheet for the seller's own view — keeps the More
                  tile's controlled-open API wired even though the seller
                  doesn't get the top-right Tip pill. */}
              {stream.status === "live" && user && user.user_id === stream.seller_id && (
                <TipSheet
                  streamId={stream.id}
                  sellerName={stream.seller_name}
                  user={user}
                  openOverride={tipSheetOpen}
                  onOpenOverrideChange={setTipSheetOpen}
                  hideTrigger
                />
              )}

              {/* Scheduled pill */}
              {stream.status === "scheduled" && (
                <div className="absolute top-3 left-1/2 -translate-x-1/2 pill pill-scheduled z-10">SCHEDULED</div>
              )}

              {/* Private show pill — visible to seller AND viewers so everyone
                  knows the show is link-only. Pinned just below the seller
                  card so it doesn't fight the viewer pill. */}
              {stream.visibility === "private" && (
                <div
                  className="absolute left-3 z-20 inline-flex items-center gap-1 rounded-full bg-zinc-900/85 backdrop-blur text-white text-[10px] font-bold px-2.5 py-1 uppercase tracking-wider shadow-lg border border-white/15"
                  style={{ top: "calc(max(var(--sai-top, env(safe-area-inset-top, 0px)), 28px) + 60px)" }}
                  data-testid="private-show-pill"
                  title="This is a private (unlisted) show — link-only access"
                >
                  🔒 Private show
                </div>
              )}

              {/* Seller: Go live now */}
              {user && user.user_id === stream.seller_id && stream.status === "scheduled" && (
                <div className="absolute inset-x-0 top-1/3 flex justify-center z-20">
                  <Button
                    onClick={startNow}
                    className="bg-red-600 hover:bg-red-700 text-white h-12 px-7 text-sm font-bold tracking-wide rounded-full shadow-lg shadow-red-500/40"
                    data-testid="start-stream-now-btn"
                  >
                    Go live now
                  </Button>
                </div>
              )}

              {/* Seller: End stream — pushed well clear of the iOS status bar
                  (battery/wifi/time) by adding a generous safe-area offset.
                  On desktop it stays compact in the top-right of the video. */}
              {/* Mod manager — pre-PC redesign this lived as a wide pill
                  at the top of the video. It's now folded into the desktop
                  right rail (a single circle icon, matching the visual
                  language of the rest of the rail). Mobile still uses the
                  More sheet entry. So this block intentionally renders
                  nothing — the entry points are downstream. */}

              {user && user.user_id === stream.seller_id && stream.status === "live" && (
                <div
                  className="absolute right-3 z-30 md:right-4 flex items-center gap-2"
                  style={{ top: "calc(max(var(--sai-top, env(safe-area-inset-top, 0px)), 28px) + 12px)" }}
                >
                  <Button
                    onClick={endStream}
                    className="bg-red-600/90 hover:bg-red-600 border border-white/20 text-white h-9 px-4 text-xs font-bold rounded-full backdrop-blur shadow-lg"
                    data-testid="end-stream-btn"
                  >
                    <SignOutIcon size={14} weight="bold" className="mr-1.5" /> End stream
                  </Button>
                </div>
              )}

              {/* Seller: toolbar while paused — Resume + End only. */}
              {user && user.user_id === stream.seller_id && stream.status === "paused" && (
                <div
                  className="absolute right-3 z-30 md:right-4 flex items-center gap-2"
                  style={{ top: "calc(max(var(--sai-top, env(safe-area-inset-top, 0px)), 28px) + 12px)" }}
                >
                  <Button
                    onClick={endStream}
                    className="bg-red-600/90 hover:bg-red-600 border border-white/20 text-white h-9 px-4 text-xs font-bold rounded-full backdrop-blur shadow-lg"
                    data-testid="end-stream-from-paused-btn"
                  >
                    <SignOutIcon size={14} weight="bold" className="mr-1.5" /> End stream
                  </Button>
                </div>
              )}

              {/* Paused overlay — shown to EVERYONE (seller + viewers) while
                  the stream is paused. Covers the video so nobody sees a
                  frozen frame. Seller's Resume control is in the toolbar
                  above; viewers just wait. */}
              {stream.status === "paused" && (
                <div
                  className="absolute inset-0 z-20 bg-zinc-950/85 backdrop-blur-md flex flex-col items-center justify-center gap-4 text-white px-8 text-center"
                  data-testid="stream-paused-overlay"
                >
                  <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-amber-500/20 border-2 border-amber-400">
                    <span className="text-3xl">⏸</span>
                  </div>
                  <div className="font-display font-black text-3xl tracking-tight">
                    Show paused
                  </div>
                  <div className="text-sm text-zinc-300 max-w-sm">
                    {user && user.user_id === stream.seller_id
                      ? "Tap Resume above when you're ready to go back live. Viewers are waiting in the room."
                      : "The seller stepped away for a moment. We'll be back live shortly — stay right here."}
                  </div>
                  {user && user.user_id === stream.seller_id && (
                    <Button
                      onClick={resumeStream}
                      className="mt-2 bg-emerald-500 hover:bg-emerald-400 text-zinc-900 h-11 px-7 rounded-full font-bold"
                      data-testid="paused-overlay-resume-btn"
                    >
                      ▶ Resume show
                    </Button>
                  )}
                </div>
              )}

              {/* Moderator: flag stream — removed from on-stream UI per
                  product call. Moderators can flag from the admin queue
                  instead so the live show stays uncluttered for viewers. */}

              {/* Report button moved into the mobile "More" bottom sheet
                  (`more-report-btn`) to keep the top-right of the stream
                  uncluttered. */}

              {/* Floating icon rail — DESKTOP. Mirrors the mobile rail
                  exactly: Share / Sound / Wallet / Shop / More.  Secondary
                  actions (Tip / Bidoria / Reactions / Mods / Pause / Flip)
                  live inside the "More" bottom sheet so both surfaces
                  stay visually identical. */}
              <div
                className="hidden md:flex absolute right-3 top-1/2 -translate-y-1/2 flex-col items-center gap-6 z-10"
                style={{ filter: "drop-shadow(0 2px 8px rgba(0,0,0,0.6))" }}
                data-testid="desktop-rail"
              >
                {/* Cinema 4K — seller only. Mirrors the mobile behaviour:
                    a non-clickable sparkle badge when Cinema is live, or
                    an upsell entry-point when the seller is on a non-Plus
                    plan. Hidden completely when Mux isn't configured. */}
                {user?.user_id === stream.seller_id && muxEnabled && (
                  isCinemaEligible ? (
                    <div
                      className="relative flex flex-col items-center gap-1 pointer-events-none select-none"
                      data-testid="desktop-cinema-active-badge"
                      aria-label="Cinema 4K active"
                    >
                      <span className="relative inline-flex items-center gap-1 text-[10px] font-black tracking-tight leading-none px-2 py-1.5 rounded-md bg-gradient-to-br from-amber-300 via-amber-400 to-orange-500 text-zinc-950 shadow-lg ring-1 ring-amber-200/40">
                        <SparkleIcon size={12} weight="fill" className="cinema-sparkle text-zinc-950" />
                        4K
                        <span aria-hidden className="absolute -inset-0.5 rounded-md ring-2 ring-amber-300/40 animate-pulse" />
                      </span>
                      <span className="text-[11px] font-semibold text-white tracking-tight leading-none">
                        Cinema
                      </span>
                    </div>
                  ) : (
                    <RailButton
                      icon={
                        <span className="text-[10px] font-black tracking-tight leading-none px-1.5 py-1 rounded-md bg-amber-400 text-zinc-950 shadow">
                          4K
                        </span>
                      }
                      label="Cinema"
                      testid="desktop-cinema-upsell-btn"
                      onClick={() => setPlusUpgradeOpen(true)}
                    />
                  )
                )}
                <RailButton
                  icon={<ShareFatIcon size={26} weight="fill" />}
                  label="Share"
                  testid="desktop-share-btn"
                  onClick={() => setShareSheetOpen(true)}
                />
                <RailButton
                  icon={muted ? <SpeakerSlashIcon size={26} weight="fill" /> : <SpeakerHighIcon size={26} weight="fill" />}
                  label={muted ? "Muted" : "Sound"}
                  testid="desktop-sound-btn"
                  onClick={() => {
                    const v = document.querySelector('video[data-stream-video]');
                    if (v) {
                      v.muted = !v.muted;
                      setMuted(v.muted);
                    } else {
                      setMuted((m) => !m);
                    }
                  }}
                />
                <RailButton
                  icon={<WalletIcon size={26} weight="fill" />}
                  label="Wallet"
                  testid="desktop-wallet-btn"
                  onClick={() => setWalletSheetOpen(true)}
                />
                <RailButton
                  icon={<StorefrontIcon size={26} weight="fill" />}
                  label="Shop"
                  testid="desktop-shop-rail-btn"
                  onClick={() => setMobileShopOpen(true)}
                />
                {/* Auto-detect — Plus YEARLY only. Visible to every
                    seller; tapping when ineligible opens the
                    PlusUpgradeSheet. Eligible sellers get an on/off
                    toggle that drives the background detection loop. */}
                {isOwner && stream.status === "live" && (
                  <button
                    type="button"
                    onClick={handleDetectRailTap}
                    className="relative flex flex-col items-center gap-1 group focus:outline-none"
                    data-testid="desktop-autodetect-btn"
                    data-active={detectEligible && autoDetectEnabled ? "1" : "0"}
                    data-eligible={detectEligible ? "1" : "0"}
                    aria-label={
                      detectEligible
                        ? (autoDetectEnabled ? "Auto-detect ON — tap to pause" : "Auto-detect OFF — tap to enable")
                        : "Auto-Detect — yearly Plus only, tap to upgrade"
                    }
                    title={
                      detectEligible
                        ? (autoDetectEnabled ? "Auto-detect ON — tap to pause" : "Auto-detect OFF — tap to enable")
                        : "Bidorah Plus yearly only — tap to upgrade"
                    }
                  >
                    <span className={`relative h-11 w-11 rounded-full grid place-items-center text-white border shadow-md ${
                      detectEligible && autoDetectEnabled
                        ? "bg-gradient-to-br from-[#1D78AB] via-[#1D78AB] to-[#0F3D5C] border-white/20"
                        : "bg-zinc-900/55 backdrop-blur-sm border-white/15"
                    }`}>
                      <SparkleIcon size={20} weight="fill" className={detectEligible && autoDetectEnabled ? "cinema-sparkle" : ""} />
                      {detectEligible && autoDetectEnabled && autoDetect.active && (
                        <span aria-hidden className="absolute inset-0 rounded-full ring-2 ring-[#1D78AB]/40 animate-pulse" />
                      )}
                      {!detectEligible && (
                        <span aria-hidden className="absolute -top-1 -right-1 h-4 px-1 rounded-full bg-amber-400 text-zinc-950 text-[8px] font-black tracking-wider grid place-items-center">
                          +
                        </span>
                      )}
                    </span>
                    <span className="text-[11px] font-semibold text-white tracking-tight leading-none">
                      {detectEligible ? (autoDetectEnabled ? "Detect" : "Off") : "Detect"}
                    </span>
                  </button>
                )}
                <RailButton
                  icon={<DotsThreeIcon size={32} weight="bold" />}
                  label="More"
                  testid="desktop-more-btn"
                  onClick={() => setMobileMoreOpen(true)}
                />
              </div>

              {/* Active lot — bottom overlay (live-shopping pattern). Three
                  visual states: ACTIVE auction (bid card), SOLD (winner banner
                  + horizontal item row matching the live-shopping reference),
                  and NONE (only the awaiting pill renders). */}
              {activeListing && activeListing.status === "active" && (
                <div className="absolute left-3 right-3 bottom-16 z-10">
                  <div className={`bg-zinc-900/85 backdrop-blur border border-white/10 rounded-2xl p-3 flex gap-3 items-center shadow-xl transition-shadow ${lotHeat === "frenzy" ? "hot-lot-card-frenzy" : lotHeat === "hot" ? "hot-lot-card-hot" : ""}`}>
                    <div className="h-14 w-14 rounded-xl bg-zinc-800 flex-shrink-0 overflow-hidden">
                      {activeListing.images?.[0] && (
                        <img src={fileUrl(activeListing.images[0])} className="h-full w-full object-cover" alt="" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="text-white font-bold text-sm truncate">{activeListing.title}</div>
                        {lotHeat !== "cold" && (
                          <HotLotBadge level={lotHeat} compact />
                        )}
                      </div>
                      <div className="text-[11px] text-zinc-400 mt-0.5 flex items-center gap-1.5 flex-wrap">
                        <span>
                          {activeListing.bid_count || 0} Bid{(activeListing.bid_count || 0) === 1 ? "" : "s"}
                        </span>
                        <span>·</span>
                        <span>Shipping + Taxes extra</span>
                        {activeListing.ai_grade_estimate?.grade != null && (
                          <span
                            className="inline-flex items-center gap-1 rounded-full bg-amber-400/20 text-amber-200 border border-amber-300/40 px-1.5 py-0.5 font-bold"
                            data-testid="active-listing-ai-grade-badge"
                            title={`AI predicted PSA ${activeListing.ai_grade_estimate.grade} · ${activeListing.ai_grade_estimate.confidence}% confidence`}
                          >
                            <SparkleIcon size={9} weight="fill" />
                            AI PSA {Number(activeListing.ai_grade_estimate.grade).toFixed(
                              activeListing.ai_grade_estimate.grade % 1 === 0 ? 0 : 1
                            )}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono font-black text-xl text-white leading-none">
                        ${(activeListing.current_bid || activeListing.starting_bid || activeListing.price || 0).toFixed(0)}
                      </div>
                      <div className="text-[10px] mt-1 uppercase tracking-wider font-bold text-emerald-400">
                        Live
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* SOLD state — winner banner + transparent item row pinned over
                  the video. No card chrome (just text/image floating on the
                  feed) matching the live-shopping reference. */}
              {activeListing && activeListing.status === "sold" && (
                <div
                  className="absolute left-0 right-0 md:bottom-24 z-10 px-4 space-y-3 pointer-events-none"
                  style={{ bottom: "calc(max(env(safe-area-inset-bottom, 0px), 24px) + 80px)" }}
                >
                  {/* Winner banner */}
                  {activeListing.highest_bidder_name && (
                    <div
                      className="flex items-center gap-2.5 drop-shadow-[0_2px_4px_rgba(0,0,0,0.85)]"
                      data-testid="winner-banner"
                    >
                      <SellerAvatar
                        name={activeListing.highest_bidder_name}
                        picture={activeListing.highest_bidder_picture}
                        size={44}
                        editable={false}
                      />
                      <div className="text-white font-bold text-base truncate">
                        <span className="truncate">{activeListing.highest_bidder_name}</span>{" "}
                        <span className="text-amber-300">won!</span>
                      </div>
                    </div>
                  )}
                  {/* Sold item row — transparent, no border */}
                  <div className="flex gap-3 items-start drop-shadow-[0_2px_4px_rgba(0,0,0,0.85)]">
                    <div className="h-20 w-20 rounded-xl bg-zinc-800/60 flex-shrink-0 overflow-hidden ring-1 ring-white/10">
                      {activeListing.images?.[0] && (
                        <img
                          src={fileUrl(activeListing.images[0])}
                          className="h-full w-full object-cover"
                          alt=""
                        />
                      )}
                    </div>
                    <div className="flex-1 min-w-0 pt-0.5">
                      <div
                        className="text-white font-extrabold text-base leading-tight truncate"
                        data-testid="sold-listing-title"
                      >
                        {activeListing.title}
                      </div>
                      {activeListing.description && (
                        <div className="text-zinc-200/85 text-[13px] mt-0.5 leading-snug line-clamp-1">
                          {activeListing.description}
                        </div>
                      )}
                      <div className="text-zinc-300/80 text-[12px] mt-1.5">
                        {Number.isFinite(activeListing.shipping_estimate) && activeListing.shipping_estimate > 0
                          ? `$${activeListing.shipping_estimate.toFixed(2)} Shipping + Taxes`
                          : "Shipping + Taxes calculated at checkout"}
                      </div>
                    </div>
                    <div className="text-right pt-0.5">
                      <div className="font-mono font-black text-2xl text-white leading-none">
                        ${(activeListing.current_bid || activeListing.price || 0).toFixed(0)}
                      </div>
                      <div className="text-[12px] mt-1.5 font-bold text-red-400">
                        Sold
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Awaiting-next-item pill — sits at the very bottom of the
                  video stack (below the chat input) so viewers glance down
                  to see status, up to the chat bar to say something. */}
              {stream.status === "live" && (!activeListing || activeListing.status === "sold") && (
                <>
                  {/* MOBILE — big rounded translucent pill */}
                  <div
                    className="md:hidden absolute left-4 right-4 z-10 pointer-events-none"
                    style={{ bottom: "calc(max(env(safe-area-inset-bottom, 0px), 48px) + 12px)" }}
                    data-testid="awaiting-next-bar"
                  >
                    <div className="h-14 rounded-full bg-zinc-900/55 backdrop-blur-sm border border-white/10 grid place-items-center text-zinc-200/80 text-base font-bold tracking-tight">
                      Awaiting Next Item
                    </div>
                  </div>
                  {/* DESKTOP — original slim amber bar */}
                  <div
                    className="hidden md:block absolute left-0 right-0 bottom-0 bg-amber-400 text-zinc-900 font-bold text-center text-[13px] py-2 tracking-tight z-10"
                  >
                    Awaiting next item
                  </div>
                </>
              )}

              {/* Bid input — pinned below the card when auction is open */}
              {activeListing && activeListing.type === "auction" && activeListing.status === "active" && stream.status === "live" && (
                <div className="absolute left-3 right-3 bottom-3 z-20 flex gap-2 items-center">
                  <Input
                    type="number"
                    min={minBid}
                    value={bidInput}
                    onChange={(e) => setBidInput(e.target.value)}
                    placeholder={`$${minBid.toFixed(0)}+`}
                    className="bg-zinc-900/90 border-white/20 text-white rounded-full h-11 px-4 flex-1 placeholder:text-zinc-400"
                    data-testid="stream-bid-input"
                  />
                  {/* BID-pill audio opt-in. Subtle, tucked between the input
                      and the BID button. Tap once to unlock the sparkler
                      crackle (last 7s) + BOOM (at 0s). Choice persists in
                      localStorage. */}
                  <button
                    type="button"
                    onClick={() => setBidSoundOn((v) => !v)}
                    className={`h-9 w-9 grid place-items-center rounded-full border transition shrink-0 ${
                      bidSoundOn
                        ? "bg-amber-300 text-amber-950 border-amber-200 shadow-[0_0_0_3px_rgba(252,211,77,0.18)]"
                        : "bg-zinc-900/85 text-zinc-300 border-white/15 hover:bg-zinc-800"
                    }`}
                    aria-pressed={bidSoundOn}
                    aria-label={bidSoundOn ? "Mute bid sound effects" : "Enable bid sound effects"}
                    title={bidSoundOn ? "Bid sounds on — tap to mute" : "Bid sounds off — tap to enable"}
                    data-testid="bid-sound-toggle"
                  >
                    {bidSoundOn ? <SpeakerHighIcon size={16} weight="fill" /> : <SpeakerSlashIcon size={16} weight="fill" />}
                  </button>
                  <FirePill secondsLeft={bidSecondsLeft} muted={!bidSoundOn}>
                    <Button
                      onClick={placeBid}
                      className="bg-[#1D78AB] hover:bg-[#165d85] text-white rounded-full h-11 px-6 font-bold shadow-lg shadow-[#1D78AB]/40"
                      data-testid="stream-place-bid-btn"
                    >
                      BID
                    </Button>
                  </FirePill>
                  {activeListing?.buy_now_price && user?.user_id !== stream.seller_id && (
                    <Button
                      onClick={buyNow}
                      className="bg-amber-500 hover:bg-amber-400 text-zinc-950 rounded-full h-11 px-5 font-black shadow-lg"
                      data-testid="stream-buy-now-btn"
                      title={
                        defaultCard
                          ? `One-tap pay with ${defaultCard.brand.toUpperCase()} •••• ${defaultCard.last4}`
                          : "Instant Buy-It-Now — bypass the auction"
                      }
                    >
                      {defaultCard ? (
                        <>BUY ${Number(activeListing.buy_now_price).toFixed(0)} · •••• {defaultCard.last4}</>
                      ) : (
                        <>BUY ${Number(activeListing.buy_now_price).toFixed(0)}</>
                      )}
                    </Button>
                  )}
                </div>
              )}

              {/* ===== MOBILE-ONLY overlays (hidden md:hidden) ===== */}

              {/* Mobile chat — last 5 messages. Floats in the TOP slot of
                  the bottom stack: chat input is just above the awaiting
                  pill, and messages float above the input. */}
              <div
                className={`md:hidden absolute left-3 right-14 z-10 pointer-events-none transition-opacity duration-200 ${
                  immersive ? "opacity-0 pointer-events-none" : "opacity-100"
                }`}
                style={{
                  bottom: "calc(max(env(safe-area-inset-bottom, 0px), 16px) + 200px)",
                  // Cinematic fade: oldest messages (top of the stack) fade
                  // into the video, newest (bottom) stay fully visible.
                  // The vendor prefix covers WebKit (iOS Safari, Capacitor
                  // WebView); the standard `maskImage` is React-camelcased.
                  WebkitMaskImage: "linear-gradient(to top, rgba(0,0,0,1) 55%, rgba(0,0,0,0.4) 80%, rgba(0,0,0,0) 100%)",
                  maskImage: "linear-gradient(to top, rgba(0,0,0,1) 55%, rgba(0,0,0,0.4) 80%, rgba(0,0,0,0) 100%)",
                }}
                data-testid="mobile-chat-overlay"
                aria-hidden={immersive ? "true" : "false"}
              >
                <div className="max-h-[34vh] overflow-hidden space-y-1.5 pointer-events-auto">
                  {messages
                    .filter((m) => m.kind !== "presence")
                    .slice(-5)
                    .map((m) =>
                      m.kind === "grade" ? (
                        <div key={m.id} className="max-w-[88%]">
                          <GradeChatMessage message={m} />
                        </div>
                      ) : m.kind === "tip" ? (
                        <div
                          key={m.id}
                          className="flex items-center gap-1.5 bg-emerald-500/80 backdrop-blur-sm rounded-full px-2.5 py-1 text-xs text-white max-w-fit"
                        >
                          💚 <span className="font-bold">{m.user_name}</span>
                          <span>${Number(m.amount || 0).toFixed(0)}</span>
                        </div>
                      ) : (
                        <div
                          key={m.id}
                          className="flex items-start gap-2 max-w-[80%]"
                          data-testid={`mobile-chat-msg-${m.id}`}
                        >
                          <SellerAvatar
                            name={m.user_name || "Guest"}
                            picture={m.picture}
                            size={26}
                            className="shrink-0"
                          />
                          <div className="min-w-0">
                            <div className="text-[11px] text-white/80 font-semibold">
                              {m.user_id && m.user_name ? (
                                <button
                                  type="button"
                                  onClick={() => replyToInChat(m.user_name)}
                                  className="hover:underline focus:outline-none"
                                  data-testid={`mobile-chat-msg-author-${m.id}`}
                                  title={`Reply to ${m.user_name}`}
                                >
                                  {m.user_name}
                                </button>
                              ) : (
                                m.user_name
                              )}
                              {m.is_founder && (
                                <FounderPill is_founder size="xs" className="ml-1 align-middle" />
                              )}
                              {m.is_admin && (
                                <span className="ml-1 text-[9px] bg-zinc-600/80 text-white rounded-full px-1.5 py-0.5">
                                  Mod
                                </span>
                              )}
                            </div>
                            <div className="text-sm text-white font-medium drop-shadow-md break-words leading-snug">
                              {m.message}
                            </div>
                          </div>
                        </div>
                      )
                    )}
                </div>
              </div>

              {/* Mobile floating "Say something…" input pill.
                  Sits ABOVE the Awaiting-Next-Item pill: stack reads
                  (bottom→top): footer → awaiting → chat input → messages.
                  Logged-out users get a sign-in prompt. Available across
                  every active stream state (preparing / scheduled / live /
                  paused) so viewers can chat both before and during the
                  show — only hidden once the stream has fully ended.

                  When an active auction listing is being featured we
                  lift the composer further so it never overlaps the
                  bottom auction card. The card sits at `bottom-16` (64px)
                  and is ~80px tall → its TOP edge is at ~144px. We park
                  the composer 12px above that for breathing room. */}
              {/* Floating chat composer for mobile. Sits ABOVE the
                  active-auction card via z-index — we no longer lift the
                  bottom offset when an auction is featured (per product
                  call: the composer must stay anchored so muscle memory
                  is preserved and the chat stays "on top of" the auction
                  tab visually rather than jumping around). */}
              {stream.status !== "ended" && (
                <div
                  className={`md:hidden absolute left-3 right-14 z-30 transition-opacity duration-200 ${
                    immersive ? "opacity-0 pointer-events-none" : "opacity-100"
                  }`}
                  style={{
                    bottom: "calc(max(env(safe-area-inset-bottom, 0px), 48px) + 100px)",
                  }}
                  data-testid="mobile-floating-chat-bar"
                  aria-hidden={immersive ? "true" : "false"}
                >
                  {user ? (
                    <div className="flex items-center gap-2">
                      <textarea
                        value={chatInput}
                        onChange={(e) => setChatInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            sendChat();
                          }
                        }}
                        placeholder="Say something…"
                        rows={1}
                        className="flex-1 h-11 rounded-full bg-zinc-900/55 backdrop-blur-md border border-white/20 text-white placeholder:text-white/70 px-5 text-sm shadow-lg resize-none leading-[2.5rem] overflow-hidden focus:outline-none focus:ring-2 focus:ring-[#1D78AB]/60"
                        data-testid="mobile-floating-chat-input"
                        // ⚠️ <textarea> intentionally used instead of <input> —
                        // Samsung Internet / Chrome on Android *ignore*
                        // autoComplete="off" for <input type="text">, painting
                        // the credentials/cards/location autofill bar above the
                        // keyboard. <textarea> elements are not in their
                        // autofill heuristic, so the toolbar disappears.
                        name="bidorah-chat-message"
                        autoComplete="off"
                        autoCorrect="off"
                        autoCapitalize="sentences"
                        spellCheck={false}
                        enterKeyHint="send"
                        inputMode="text"
                        data-form-type="other"
                        data-lpignore="true"
                        data-1p-ignore="true"
                      />
                      {chatInput.trim() && (
                        <button
                          onClick={sendChat}
                          className="h-11 px-4 rounded-full bg-[#1D78AB] text-white text-sm font-bold shadow-lg"
                          data-testid="mobile-floating-chat-send"
                        >
                          Send
                        </button>
                      )}
                    </div>
                  ) : (
                    <button
                      onClick={() => navigate("/login")}
                      className="w-full h-11 rounded-full bg-zinc-900/55 backdrop-blur-md border border-white/20 text-white/80 text-sm font-semibold shadow-lg"
                      data-testid="mobile-floating-chat-login"
                    >
                      Sign in to join the chat
                    </button>
                  )}
                </div>
              )}

              {/* Live activity ticker — slim mobile strip showing recent
                  non-join events (bids, double-tap likes, tips). Join
                  events now surface at the TOP-LEFT next to the cohost
                  strip via the `recent-joins-strip` element above, so we
                  filter them out here to avoid a duplicate chime. */}
              {activityFeed.filter((e) => e.kind !== "join").length > 0 && (
                <div
                  className={`md:hidden absolute left-3 right-3 z-10 pointer-events-none flex flex-col gap-1.5 items-start transition-opacity duration-200 ${
                    immersive ? "opacity-0" : "opacity-100"
                  }`}
                  style={{ bottom: "calc(max(env(safe-area-inset-bottom, 0px), 48px) + 128px)" }}
                  data-testid="activity-ticker"
                  aria-hidden={immersive ? "true" : "false"}
                >
                  {activityFeed.filter((e) => e.kind !== "join").slice(-2).map((evt) => (
                    <ActivityTickerRow key={evt.id} text={evt.text} kind={evt.kind} />
                  ))}
                </div>
              )}

              {/* Mobile "Awaiting next item" bar, sits under the comment input.
                  On desktop the old version is already rendered above. */}

              {/* Mobile right rail — collapsed for clarity. Always-visible:
                  Share, Sound, More (the rest live inside the More sheet).
                  Bidoria moved into the More sheet to match the live sale platforms
                  right-rail pattern. Seller-only primaries also kept on
                  the rail: Flip, Pause/Resume (muscle memory + frequency). */}
              <div
                className={`md:hidden absolute right-3 top-1/2 -translate-y-1/2 flex flex-col items-center gap-3 z-10 transition-opacity duration-200 ${
                  immersive ? "opacity-0 pointer-events-none" : "opacity-100"
                }`}
                style={{ filter: "drop-shadow(0 2px 8px rgba(0,0,0,0.6))" }}
                data-testid="mobile-rail"
                aria-hidden={immersive ? "true" : "false"}
              >
                {/* Cinema 4K rail tile — seller-only.
                    • When the seller is already cinema-eligible (Plus
                      subscription active) we show a non-clickable
                      animated sparkle badge to signal Cinema is LIVE.
                    • Otherwise we keep the upsell entry-point that
                      routes to /plus. Hidden entirely when Mux isn't
                      configured on this deployment.  */}
                {user?.user_id === stream.seller_id && muxEnabled && (
                  isCinemaEligible ? (
                    <div
                      className="relative flex flex-col items-center gap-1 pointer-events-none select-none"
                      data-testid="mobile-cinema-active-badge"
                      aria-label="Cinema 4K active"
                    >
                      <span className="relative h-11 w-11 rounded-full grid place-items-center bg-gradient-to-br from-amber-300 via-amber-400 to-orange-500 text-zinc-950 shadow-md border border-amber-200/40">
                        <span className="text-[11px] font-black tracking-tighter leading-none">4K</span>
                        <span aria-hidden className="absolute inset-0 rounded-full ring-2 ring-amber-300/40 animate-pulse" />
                      </span>
                      <span className="text-[11px] font-semibold text-white tracking-tight leading-none">
                        Cinema
                      </span>
                    </div>
                  ) : (
                    <RailButton
                      icon={
                        <span className="text-[11px] font-black tracking-tighter leading-none text-amber-300">
                          4K
                        </span>
                      }
                      label="Cinema"
                      testid="mobile-cinema-upsell-btn"
                      onClick={() => setPlusUpgradeOpen(true)}
                    />
                  )
                )}
                <RailButton
                  icon={<ShareFatIcon size={22} weight="fill" />}
                  label="Share"
                  testid="mobile-share-btn"
                  onClick={() => setShareSheetOpen(true)}
                />
                <RailButton
                  icon={muted ? <SpeakerSlashIcon size={22} weight="fill" /> : <SpeakerHighIcon size={22} weight="fill" />}
                  label={muted ? "Muted" : "Sound"}
                  testid="mobile-sound-btn"
                  onClick={() => {
                    const v = document.querySelector('video[data-stream-video]');
                    if (v) {
                      v.muted = !v.muted;
                      setMuted(v.muted);
                    } else {
                      setMuted((m) => !m);
                    }
                  }}
                />
                {/* Pause / Resume — mobile right rail, seller only. Stays
                    visible because sellers reach for it constantly mid-show. */}
                {user?.user_id === stream.seller_id && stream.status === "live" && (
                  <RailButton
                    icon={<span className="text-lg leading-none font-black">⏸</span>}
                    label="Pause"
                    testid="mobile-pause-btn"
                    onClick={pauseStream}
                  />
                )}
                {user?.user_id === stream.seller_id && stream.status === "paused" && (
                  <RailButton
                    icon={<span className="text-lg leading-none font-black">▶</span>}
                    label="Resume"
                    testid="mobile-resume-btn"
                    onClick={resumeStream}
                  />
                )}
                {/* Camera flip — always visible for the seller. Tapping before
                    go-live pre-selects which lens (rear / selfie) the camera
                    starts with. Tapping while live re-publishes the track. */}
                {user?.user_id === stream.seller_id && (
                  <RailButton
                    icon={<ArrowsClockwiseIcon size={22} weight="bold" />}
                    label="Flip"
                    testid="mobile-flip-camera-btn"
                    onClick={() => window.dispatchEvent(new CustomEvent("seller:flip-camera"))}
                  />
                )}
                {/* Wallet — live sale platforms-style in-stream balance sheet. Sits
                    directly above Shop so users can glance at their
                    credits without leaving the broadcast. */}
                <RailButton
                  icon={<WalletIcon size={22} weight="fill" />}
                  label="Wallet"
                  testid="mobile-wallet-btn"
                  onClick={() => setWalletSheetOpen(true)}
                />
                {/* Shop — opens the live show's product drawer.  Kept on
                    the primary rail (under Wallet) so the seller's shop is
                    always one tap away, not buried in More. */}
                <RailButton
                  icon={<StorefrontIcon size={22} weight="fill" />}
                  label="Shop"
                  testid="mobile-shop-btn"
                  onClick={() => setMobileShopOpen(true)}
                />
                {/* Auto-detect — Plus YEARLY only. Visible to every
                    seller; tapping when ineligible opens the upgrade
                    sheet locked to yearly. */}
                {isOwner && stream.status === "live" && (
                  <button
                    type="button"
                    onClick={handleDetectRailTap}
                    className="relative flex flex-col items-center gap-1 group focus:outline-none"
                    data-testid="mobile-autodetect-btn"
                    data-active={detectEligible && autoDetectEnabled ? "1" : "0"}
                    data-eligible={detectEligible ? "1" : "0"}
                    aria-label={
                      detectEligible
                        ? (autoDetectEnabled ? "Auto-detect ON — tap to pause" : "Auto-detect OFF — tap to enable")
                        : "Auto-Detect — yearly Plus only, tap to upgrade"
                    }
                  >
                    <span className={`relative h-11 w-11 rounded-full grid place-items-center text-white border shadow-md ${
                      detectEligible && autoDetectEnabled
                        ? "bg-gradient-to-br from-[#1D78AB] via-[#1D78AB] to-[#0F3D5C] border-white/20"
                        : "bg-zinc-900/55 backdrop-blur-sm border-white/15"
                    }`}>
                      <SparkleIcon size={20} weight="fill" className={detectEligible && autoDetectEnabled ? "cinema-sparkle" : ""} />
                      {detectEligible && autoDetectEnabled && autoDetect.active && (
                        <span aria-hidden className="absolute inset-0 rounded-full ring-2 ring-[#1D78AB]/40 animate-pulse" />
                      )}
                      {!detectEligible && (
                        <span aria-hidden className="absolute -top-1 -right-1 h-4 px-1 rounded-full bg-amber-400 text-zinc-950 text-[8px] font-black tracking-wider grid place-items-center">
                          +
                        </span>
                      )}
                    </span>
                    <span className="text-[11px] font-semibold text-white tracking-tight leading-none">
                      {detectEligible ? (autoDetectEnabled ? "Detect" : "Off") : "Detect"}
                    </span>
                  </button>
                )}
                {/* MORE — opens the bottom sheet with secondary actions. */}
                <RailButton
                  icon={<DotsThreeIcon size={26} weight="bold" />}
                  label="More"
                  testid="mobile-more-btn"
                  onClick={() => setMobileMoreOpen(true)}
                />
              </div>
            </div>
          </div>
        </section>

        {/* ---------- RIGHT: Chat + Watching tabs (desktop only) ---------- */}
        <aside
          className={`border-l border-zinc-200 bg-white flex-col max-h-[calc(100vh-64px)] ${
            immersive ? "hidden" : "hidden md:flex"
          }`}
          data-testid="stream-chat-rail"
        >
          <div className="flex border-b border-zinc-200">
            <button
              type="button"
              onClick={() => setRightTab("chat")}
              className={`flex-1 py-3 text-sm font-semibold transition ${
                rightTab === "chat"
                  ? "text-zinc-900 border-b-2 border-zinc-900"
                  : "text-zinc-500 hover:text-zinc-700"
              }`}
              data-testid="chat-tab-chat"
            >
              Chat
            </button>
            <button
              type="button"
              onClick={() => setRightTab("watching")}
              className={`flex-1 py-3 text-sm font-semibold transition ${
                rightTab === "watching"
                  ? "text-zinc-900 border-b-2 border-zinc-900"
                  : "text-zinc-500 hover:text-zinc-700"
              }`}
              data-testid="chat-tab-watching"
            >
              Watching
              <span className="ml-1.5 text-xs font-normal text-zinc-400">
                {liveViewers}
              </span>
            </button>
          </div>

          {rightTab === "chat" ? (
            <>
              {/* Pinned message banner — sticky at the top of chat. Only
                  ever shows one message at a time (server enforces this). */}
              {pinnedMessage && (
                <div className="border-b-2 border-amber-300 bg-amber-50 px-3 py-2 flex items-start gap-2" data-testid="chat-pinned-banner">
                  <PushPinIcon size={14} weight="fill" className="text-amber-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-amber-700 mb-0.5">
                      Pinned · @{pinnedMessage.user_name}
                    </div>
                    <div className="text-xs text-zinc-800 leading-snug break-words">
                      {pinnedMessage.message}
                    </div>
                  </div>
                  {isStreamMod && (
                    <button
                      type="button"
                      onClick={() => api.post(`/streams/${id}/mod/chat/unpin`).catch(() => {})}
                      className="text-[10px] font-mono uppercase tracking-wider text-amber-700 hover:underline flex-shrink-0"
                      data-testid="chat-pinned-unpin"
                    >
                      Unpin
                    </button>
                  )}
                </div>
              )}
              <div ref={chatRef} className="flex-1 overflow-y-auto p-3 space-y-2.5" data-testid="chat-feed">
                {messages.length === 0 && (
                  <p className="text-xs text-zinc-400 text-center py-8">Be the first to say hi.</p>
                )}
                {messages
                  .filter((m) => !m.is_hidden || isStreamMod)
                  .map((m) =>
                  m.kind === "presence" ? (
                    <div
                      key={m.id}
                      className="text-[11px] flex items-center gap-1.5 text-zinc-500 py-0.5"
                      data-testid={`presence-${m.event}`}
                    >
                      {m.event === "join" ? (
                        <SignInIcon size={12} className="text-emerald-500" />
                      ) : (
                        <SignOutIcon size={12} className="text-zinc-400" />
                      )}
                      <span>
                        {m.user_name || "Guest"} {m.event === "join" ? "joined" : "left"}
                      </span>
                    </div>
                  ) : m.kind === "grade" ? (
                    <div key={m.id} data-testid={`chat-grade-${m.id}`}>
                      <GradeChatMessage message={m} />
                    </div>
                  ) : m.kind === "tip" ? (
                    <div
                      key={m.id}
                      className="text-xs flex items-center gap-2 py-1.5 bg-emerald-50 rounded-xl px-2.5 border border-emerald-200"
                      data-testid={`chat-tip-${m.id}`}
                    >
                      <span role="img" aria-label="tip" className="text-base">💚</span>
                      <span className="text-zinc-900 font-semibold">{m.user_name}</span>
                      <span className="text-zinc-500">tipped</span>
                      <span className="font-bold text-emerald-700">${Number(m.amount || 0).toFixed(2)}</span>
                      {m.message && <span className="text-zinc-700 italic truncate">— {m.message}</span>}
                    </div>
                  ) : (
                    <div
                      key={m.id}
                      className={`flex items-start gap-2.5 bid-flash group ${
                        m.pinned
                          ? "bg-amber-50 border border-amber-200 rounded-lg p-2 -mx-2"
                          : m.is_hidden && isStreamMod
                          ? "opacity-60 border border-dashed border-zinc-300 rounded-lg p-2 -mx-2"
                          : ""
                      }`}
                      data-testid={`chat-msg-${m.id}`}
                    >
                      <SellerAvatar name={m.user_name || "Guest"} picture={m.picture} size={28} sparkle={!!m.avatar_sparkle} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <UserNamePopover
                            userId={m.user_id}
                            username={m.user_name}
                            displayName={m.user_name}
                            onReply={replyToInChat}
                            tapToReply
                            isStreamMod={isStreamMod}
                            streamId={id}
                            sellerId={stream?.seller_id}
                            currentUserId={user?.user_id}
                            className="text-xs font-semibold text-zinc-500 truncate hover:text-[#1D78AB]"
                            testid={`chat-msg-author-${m.id}`}
                          >
                            {m.user_name}
                          </UserNamePopover>
                          {m.is_admin && <AdminBadge size={12} />}
                          {(m.is_mod || streamMods.some((sm) => sm.user_id === m.user_id)) && (
                            <span className="pill bg-[#1D78AB] text-white text-[9px] uppercase tracking-widest px-1.5 py-0.5">MOD</span>
                          )}
                          {m.pinned && (
                            <span className="pill bg-amber-500 text-white text-[9px] uppercase tracking-widest px-1.5 py-0.5 inline-flex items-center gap-0.5">
                              <PushPinIcon size={9} weight="fill" /> PINNED
                            </span>
                          )}
                          {m.is_hidden && isStreamMod && (
                            <span className="pill bg-zinc-200 text-zinc-700 text-[9px] uppercase tracking-widest px-1.5 py-0.5">
                              HIDDEN
                            </span>
                          )}
                        </div>
                        {m.is_deleted ? (
                          <div className="text-xs italic text-zinc-400">
                            [message removed by moderator{m.deleted_by ? ` · ${m.deleted_by}` : ""}]
                          </div>
                        ) : (
                          <div className="text-sm text-zinc-900 leading-snug break-words">
                            {m.message}
                          </div>
                        )}
                      </div>
                      {isStreamMod && !m.is_deleted && m.user_id && m.user_id !== user?.user_id && m.user_id !== stream?.seller_id && (
                        <ChatMessageModActions
                          streamId={id}
                          message={m}
                          isCurrentlyPinned={!!m.pinned}
                        />
                      )}
                    </div>
                  )
                )}
              </div>
              <div className="p-3 border-t border-zinc-200 bg-white">
                {user ? (
                  <div className="flex gap-2">
                    <textarea
                      ref={chatInputRef}
                      value={chatInput}
                      onChange={(e) => setChatInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          sendChat();
                        }
                      }}
                      placeholder="Say something…"
                      rows={1}
                      className="flex-1 rounded-full h-10 px-4 bg-zinc-100 border border-zinc-200 text-sm resize-none leading-[2.25rem] overflow-hidden focus:outline-none focus:ring-2 focus:ring-[#1D78AB]/40"
                      data-testid="chat-input"
                      // ⚠️ <textarea> instead of <input> — Samsung/Chrome ignore
                      // autoComplete="off" on text inputs but DO respect it for
                      // textareas, killing the credentials/cards autofill bar.
                      name="bidorah-chat-message"
                      autoComplete="off"
                      autoCorrect="off"
                      autoCapitalize="sentences"
                      spellCheck={false}
                      enterKeyHint="send"
                      inputMode="text"
                      data-form-type="other"
                      data-lpignore="true"
                      data-1p-ignore="true"
                    />
                    <Button
                      onClick={sendChat}
                      className="bg-[#1D78AB] hover:bg-[#165d85] rounded-full h-10 w-10 p-0 flex-shrink-0"
                      data-testid="chat-send-btn"
                    >
                      <PaperPlaneTiltIcon size={16} />
                    </Button>
                  </div>
                ) : (
                  <Link to="/login" className="block text-center text-sm text-[#1D78AB] py-2 font-semibold">
                    Log in to join the chat
                  </Link>
                )}
              </div>
            </>
          ) : (
            <WatchingTab streamId={id} liveCount={liveViewers} refreshKey={0} onReply={replyToInChat} lastPresence={lastPresence} isStreamMod={isStreamMod} sellerId={stream?.seller_id} currentUserId={user?.user_id} streamMods={streamMods} />
          )}
        </aside>
      </main>
      {/* ===== Shop drawer (mobile + desktop) ===== */}
      {mobileShopOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end md:items-center md:justify-center"
          onClick={() => setMobileShopOpen(false)}
          data-testid="mobile-shop-drawer"
        >
          <div
            className="bg-white w-full md:w-[480px] md:max-w-[92vw] rounded-t-3xl md:rounded-3xl max-h-[85vh] md:max-h-[78vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white px-5 py-4 flex items-center justify-between border-b border-zinc-200">
              <div className="font-display font-black text-xl">Shop</div>
              <button
                onClick={() => setMobileShopOpen(false)}
                className="h-9 w-9 rounded-full bg-zinc-100 grid place-items-center"
                data-testid="mobile-shop-close"
              >
                <XIcon size={18} weight="bold" />
              </button>
            </div>
            <div className="p-4 space-y-3">
              {/* SELLER-ONLY: prominent "+ Add new item" CTA so it's a one-tap
                  jump to the listing creator with auto-return back to the live
                  stream. The fastest possible "live-shopping add inventory" UX. */}
              {user && user.user_id === stream.seller_id && (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      setMobileShopOpen(false);
                      setShowQuickAdd(true);
                    }}
                    className="block w-full rounded-2xl border-2 border-dashed border-[#1D78AB] bg-[#1D78AB]/5 p-4 text-center hover:bg-[#1D78AB]/10"
                    data-testid="mobile-shop-add-item"
                  >
                    <div className="font-display font-black text-base text-[#1D78AB] flex items-center justify-center gap-1.5">
                      <span className="text-xl leading-none">+</span> Add new item to live
                    </div>
                    <div className="text-[11px] text-zinc-500 mt-1">
                      Snap a photo, set a starting bid, drop it on stream in seconds.
                    </div>
                  </button>
                  {/* Mobile twin of the desktop "Import from Shop" — bulk-
                      attaches existing shop listings to this live show. */}
                  <button
                    type="button"
                    onClick={() => {
                      setMobileShopOpen(false);
                      setShowImportShop(true);
                    }}
                    className="block w-full rounded-2xl border border-zinc-300 bg-white p-3.5 text-left hover:border-zinc-500"
                    data-testid="mobile-shop-import-btn"
                  >
                    <div className="font-display font-black text-sm text-zinc-900 flex items-center gap-2">
                      <DownloadSimpleIcon size={14} weight="bold" className="text-[#1D78AB]" />
                      Import from your shop
                    </div>
                    <div className="text-[11px] text-zinc-500 mt-0.5">
                      Pull existing listings into this show in bulk.
                    </div>
                  </button>
                </>
              )}
              {(stream.listings || []).length === 0 && (
                <div className="text-center py-10 text-sm text-zinc-500">
                  {user && user.user_id === stream.seller_id
                    ? "No items yet. Tap the button above to add your first."
                    : "The seller hasn't dropped any items yet."}
                </div>
              )}
              {(stream.listings || []).map((l) => {
                const isSeller = user && user.user_id === stream.seller_id;
                return (
                  <div
                    key={l.id}
                    className="w-full rounded-2xl border border-zinc-200 p-3 bg-white"
                    data-testid={`mobile-shop-product-${l.id}`}
                  >
                    <div className="flex gap-3">
                      <div className="h-16 w-16 rounded-xl bg-zinc-100 flex-shrink-0 overflow-hidden">
                        {l.images?.[0] && (
                          <img src={fileUrl(l.images[0])} className="h-full w-full object-cover" alt="" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold truncate">{l.title}</div>
                        <div className="text-xs text-zinc-500 mt-0.5">
                          <span className="font-bold text-zinc-900">
                            ${(l.current_bid || l.price || 0).toFixed(0)}
                          </span>
                          <span className="mx-1.5">·</span>
                          {l.bid_count || 0} bid{(l.bid_count || 0) === 1 ? "" : "s"}
                          {l.status === "sold" && (
                            <span className="ml-2 text-emerald-600 font-bold">SOLD</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="mt-2.5 flex gap-2">
                      <button
                        onClick={() => {
                          setActiveListing(l);
                          setMobileShopOpen(false);
                        }}
                        className="flex-1 h-9 rounded-full bg-[#1D78AB] text-white text-xs font-bold"
                        data-testid={`mobile-shop-feature-${l.id}`}
                      >
                        {isSeller ? "Feature on stream" : "View / Bid"}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {mobileChatOpen && (
        <div
          className="md:hidden fixed inset-0 z-50 flex items-end pointer-events-none"
          data-testid="mobile-chat-drawer"
        >
          {/* Tap-outside catcher — fully transparent so the live video remains
              completely visible behind the chat sheet. No blur, no dim. */}
          <button
            onClick={() => setMobileChatOpen(false)}
            className="absolute inset-0 bg-transparent pointer-events-auto"
            aria-label="Close chat"
            data-testid="mobile-chat-backdrop"
          />
          <div
            className="relative pointer-events-auto bg-zinc-950/70 backdrop-blur-md w-full rounded-t-3xl h-[55vh] flex flex-col border-t border-white/10 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-zinc-950/40 px-5 py-3 flex items-center justify-between border-b border-white/10 rounded-t-3xl">
              <div className="font-display font-black text-base text-white">
                Chat <span className="text-white/50 text-xs font-normal">· {liveViewers} watching</span>
              </div>
              <button
                onClick={() => setMobileChatOpen(false)}
                className="h-8 w-8 rounded-full bg-white/10 grid place-items-center text-white"
                data-testid="mobile-chat-close"
              >
                <XIcon size={16} weight="bold" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-3 space-y-2">
              {messages.map((m) =>
                m.kind === "presence" ? (
                  <div key={m.id} className="text-[11px] text-white/60">
                    {m.user_name || "Guest"} {m.event === "join" ? "joined" : "left"}
                  </div>
                ) : m.kind === "grade" ? (
                  <div key={m.id}>
                    <GradeChatMessage message={m} />
                  </div>
                ) : m.kind === "tip" ? (
                  <div
                    key={m.id}
                    className="text-xs flex items-center gap-2 py-1.5 bg-emerald-500/20 rounded-xl px-2.5 border border-emerald-400/30 text-white"
                  >
                    <span>💚</span>
                    <span className="font-semibold">{m.user_name}</span>
                    <span className="text-white/70">tipped</span>
                    <span className="font-bold text-emerald-300">${Number(m.amount || 0).toFixed(2)}</span>
                  </div>
                ) : (
                  <div key={m.id} className="flex items-start gap-2.5">
                    <SellerAvatar name={m.user_name || "Guest"} picture={m.picture} size={26} sparkle={!!m.avatar_sparkle} />
                    <div className="flex-1 min-w-0">
                      <div className="text-[11px] font-semibold text-white/70">{m.user_name}</div>
                      <div className="text-[13px] text-white leading-snug break-words">{m.message}</div>
                    </div>
                  </div>
                )
              )}
            </div>
            <div className="p-3 border-t border-white/10">
              {user ? (
                <div className="flex gap-2">
                  <Input
                    ref={mobileChatInputRef}
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        sendChat();
                      }
                    }}
                    placeholder="Say something…"
                    className="rounded-full h-10 px-4 bg-white/10 border-white/15 text-white placeholder:text-white/50"
                    name="bidorah-chat-message"
                    type="text"
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="sentences"
                    enterKeyHint="send"
                    data-form-type="other"
                    data-lpignore="true"
                    data-1p-ignore="true"
                  />
                  <Button
                    onClick={sendChat}
                    className="bg-[#1D78AB] hover:bg-[#165d85] rounded-full h-10 w-10 p-0 flex-shrink-0"
                  >
                    <PaperPlaneTiltIcon size={16} />
                  </Button>
                </div>
              ) : (
                <Link to="/login" className="block text-center text-sm text-white py-2 font-semibold">
                  Log in to join the chat
                </Link>
              )}
            </div>
          </div>
        </div>
      )}
      {recap && <StreamRecapModal summary={recap} onClose={() => setRecap(null)} />}
      <GraderModal
        open={graderOpen}
        onClose={() => setGraderOpen(false)}
        streamId={stream?.id}
        activeListing={activeListing}
      />
      <ShareSheet
        open={shareSheetOpen}
        onClose={() => setShareSheetOpen(false)}
        title={stream?.title || "Bidorah Live"}
        sellerName={stream?.seller_name}
      />
      {/* Wallet bottom-sheet — opened by the right-rail Wallet icon.
          Renders for both buyers and sellers; sellers see their own
          credit ledger (referral / streak / cashback). */}
      <WalletSheet
        open={walletSheetOpen}
        onOpenChange={setWalletSheetOpen}
        currentUser={user}
      />
      {/* live sale platforms-style win celebration — full-screen confetti + count-up
          overlay that fires when a lot hammers down.  Auto-clears after
          ~4.4s; only one can be on-screen at a time (keyed by listing). */}
      <WinCelebration
        win={winCelebration}
        onDone={() => setWinCelebration(null)}
      />

      {/* ───── Mobile "More" bottom sheet ─────
          Holds the secondary actions that used to clutter the right rail.
          Buttons inside are wider and have proper hit-targets — better
          mobile ergonomics than icons-only on the side. */}
      {stream && (
        <Sheet open={mobileMoreOpen} onOpenChange={setMobileMoreOpen}>
          <SheetContent
            side="bottom"
            className="rounded-t-2xl md:rounded-2xl md:max-w-[480px] md:mx-auto md:my-auto md:inset-x-0 pb-[max(env(safe-area-inset-bottom,0px),96px)] md:pb-8"
            data-testid="mobile-more-sheet"
          >
            <SheetHeader className="text-left">
              <SheetTitle className="text-base">More</SheetTitle>
            </SheetHeader>
            <div className="flex gap-1.5 mt-4">
              {/* Bidoria — Bidorah's AI co-host. Seller-only entry point,
                  and only visible when the seller's account has Plus
                  (or a Founder trial window). Non-Plus sellers and all
                  viewers do not see this tile. */}
              {user?.user_id === stream.seller_id && bidoriaUnlocked && (
                <MoreTile
                  icon={
                    <span className="h-[22px] w-[22px] grid place-items-center rounded-full bg-gradient-to-br from-amber-400 to-orange-500">
                      <SparkleIcon size={14} weight="fill" className="text-white" />
                    </span>
                  }
                  label="Bidoria"
                  testid="more-bidoria-btn"
                  onClick={() => {
                    setMobileMoreOpen(false);
                    window.dispatchEvent(new CustomEvent("bidoria:open"));
                  }}
                />
              )}
              {/* Tip — opens the Stripe-backed TipSheet via the controlled
                  `openOverride` prop so the same flow used by the top-right
                  green Tip pill kicks in. Shown to all logged-in viewers so
                  the More row stays a consistent 6-tile grid; if a seller
                  ever tries to tip their own stream the backend safely
                  rejects it. */}
              {user && (
                <MoreTile
                  icon={
                    <span className="h-[22px] w-[22px] grid place-items-center rounded-full bg-gradient-to-r from-emerald-500 to-green-600">
                      <HeartStraightIcon size={13} weight="fill" className="text-white" />
                    </span>
                  }
                  label="Tip"
                  testid="more-tip-btn"
                  onClick={() => { setMobileMoreOpen(false); setTipSheetOpen(true); }}
                />
              )}
              {/* Seller-only — Start giveaway. Disabled if one is active. */}
              {user?.user_id === stream.seller_id && stream.status === "live" && (
                <MoreTile
                  icon={<GiftIcon size={22} weight="fill" />}
                  label="Giveaway"
                  testid="more-giveaway-btn"
                  onClick={() => {
                    setMobileMoreOpen(false);
                    if (giveaway?.status === "active") {
                      toast("A giveaway is already running");
                      return;
                    }
                    setStartGiveawayOpen(true);
                  }}
                />
              )}
              {/* Seller-only — AI Condition Grader (predicts PSA grade from a card photo) */}
              {user?.user_id === stream.seller_id && stream.status === "live" && (
                <MoreTile
                  icon={
                    <span className="h-[22px] w-[22px] grid place-items-center rounded-full bg-gradient-to-br from-amber-400 to-amber-600">
                      <SparkleIcon size={13} weight="fill" className="text-zinc-950" />
                    </span>
                  }
                  label="AI grade"
                  testid="more-grade-btn"
                  onClick={() => { setMobileMoreOpen(false); setGraderOpen(true); }}
                />
              )}
              {/* Seller-only / admin-only — Mods */}
              {(user?.user_id === stream.seller_id || user?.role === "admin") && (
                <MoreTile
                  icon={<ShieldStarIcon size={22} weight="bold" />}
                  label="Mods"
                  testid="more-mods-btn"
                  onClick={() => { setMobileMoreOpen(false); setShowModManager(true); }}
                />
              )}
              {/* Seller-only — Co-host invite/end */}
              {user?.user_id === stream.seller_id && stream.status === "live" && (
                (stream.cohosts || []).length === 0 ? (
                  <MoreTile
                    icon={<UsersThreeIcon size={22} weight="fill" />}
                    label="Cohost"
                    testid="more-cohost-btn"
                    onClick={() => { setMobileMoreOpen(false); inviteCohost(); }}
                  />
                ) : (
                  <MoreTile
                    icon={<UsersThreeIcon size={22} weight="fill" />}
                    label="End cohost"
                    testid="more-end-cohost-btn"
                    onClick={() => { setMobileMoreOpen(false); removeCohost(); }}
                  />
                )
              )}
              {/* Seller-only — Pause / Resume the live show. Previously
                  pinned to the desktop right rail; folded into More so the
                  PC + mobile rails stay visually identical. */}
              {/* Pause tile removed from More — it's the second item on
                  the right rail already, so duplicating it here was
                  cluttering the sheet without adding value. The Resume
                  tile stays for paused streams as an extra recovery
                  path in case the rail collapses behind the overlay. */}
              {user?.user_id === stream.seller_id && stream.status === "paused" && (
                <MoreTile
                  icon={<span className="h-[22px] w-[22px] grid place-items-center text-base font-black text-emerald-600">▶</span>}
                  label="Resume"
                  testid="more-resume-btn"
                  onClick={() => { setMobileMoreOpen(false); resumeStream(); }}
                />
              )}
              {/* Universal — Report this stream. Shown to everyone INCLUDING
                  the seller so the tile slot stays consistent (sellers can
                  use the same flow to flag their own stream for review if
                  needed; click goes to the same admin inbox). */}
              {user && (
                <MoreTile
                  icon={<FlagIcon size={22} weight="fill" />}
                  label="Report"
                  testid="more-report-btn"
                  onClick={() => { setMobileMoreOpen(false); setReportStreamOpen(true); }}
                />
              )}
            </div>
          </SheetContent>
        </Sheet>
      )}
      {stream && (
        <ReportDialog
          open={reportStreamOpen}
          onOpenChange={setReportStreamOpen}
          targetType="stream"
          targetId={stream.id}
          targetLabel={stream.title || `Live stream by @${stream.seller_username || stream.seller_name || "seller"}`}
        />
      )}
      {stream && showModManager && (
        <StreamModManager
          streamId={id}
          onClose={() => setShowModManager(false)}
        />
      )}
      {stream && (
        <ShopHub
          open={showShopHub}
          onClose={() => setShowShopHub(false)}
          stream={stream}
          pinnedListingId={stream.pinned_listing_id || activeListing?.id}
          onChange={() => load()}
        />
      )}
      {stream && user && user.user_id === stream.seller_id && (
        <QuickAddListingModal
          open={showQuickAdd}
          onClose={() => { setShowQuickAdd(false); setQuickAddPrefill(null); }}
          streamId={stream.id}
          prefill={quickAddPrefill}
          onCreated={() => { setQuickAddPrefill(null); load(); }}
        />
      )}

      {/* Whatnot-style "Import from Shop" — seller picks existing
          listings and bulk-attaches them to this live show. The
          backend broadcasts `listings.imported` which the WS handler
          uses to refresh everyone's queue. */}
      {stream && user && user.user_id === stream.seller_id && (
        <ImportFromShopSheet
          open={showImportShop}
          onOpenChange={setShowImportShop}
          streamId={stream.id}
          onImported={() => load()}
        />
      )}

      {/* In-stream Plus upgrade — opened from the Cinema rail badge
          when the seller isn't yet Plus. Opens a new tab for the
          Stripe checkout so the live show keeps streaming. */}
      <PlusUpgradeSheet
        open={plusUpgradeOpen}
        onClose={() => { setPlusUpgradeOpen(false); setPlusUpgradeReason(null); }}
        reason={plusUpgradeReason || "Unlock Cinema 4K + Bidoria AI co-host — without leaving the show."}
        lockYearly={!!plusUpgradeReason && plusUpgradeReason.includes("Yearly")}
      />

      {/* Start-giveaway dialog — seller-only entry. Submits to
          /streams/{id}/giveaways; WS broadcast picks up the banner. */}
      {stream && user && user.user_id === stream.seller_id && (
        <StartGiveawayDialog
          open={startGiveawayOpen}
          onOpenChange={setStartGiveawayOpen}
          streamId={stream.id}
          onStarted={(g) => setGiveaway(g)}
        />
      )}

      {/* End-stream confirmation — styled modal replaces window.confirm().
          Shown only to the seller (gating happens at the end-stream button
          itself). Visible state lives in `endStreamConfirmOpen`. */}
      <AlertDialog open={endStreamConfirmOpen} onOpenChange={setEndStreamConfirmOpen}>
        <AlertDialogContent data-testid="end-stream-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>End this live show?</AlertDialogTitle>
            <AlertDialogDescription>
              Viewers will be disconnected immediately and the stream will be
              marked ended. You can still review the show wrap, winners, and
              chat replay from your dashboard afterward.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="end-stream-cancel-btn"
              disabled={endingStream}
            >
              Keep streaming
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); confirmEndStream(); }}
              data-testid="end-stream-confirm-btn"
              disabled={endingStream}
              className="bg-red-600 hover:bg-red-700 focus-visible:ring-red-600"
            >
              {endingStream ? "Ending…" : "End show"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// Inline "Watching" tab — reuses /streams/:id/viewers but returns anonymous
// count for non-sellers instead of full PII.
//
// Realtime strategy:
//  1. Initial fetch on mount + safety-net refetch every 15s.
//  2. Every WS `presence` event (join/leave) is patched directly into the
//     viewer list — no extra round-trip. The seller sees instant updates
//     even on bursty join/leave events without spamming the server.
//  3. WS reconnects (`refreshKey` bump) re-sync the list from truth source.
function WatchingTab({ streamId, liveCount, refreshKey, onReply, lastPresence, isStreamMod, sellerId, currentUserId, streamMods = [] }) {
  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState("");

  const refetch = React.useCallback(async () => {
    try {
      const { data: resp } = await api.get(`/streams/${streamId}/viewers`);
      setData(resp);
      setError("");
    } catch (err) {
      setError(apiError(err) || "Only the seller can see who's watching.");
    }
  }, [streamId]);

  // Initial load + safety-net poll.
  React.useEffect(() => {
    refetch();
    const t = setInterval(refetch, 15000);
    return () => clearInterval(t);
  }, [refetch, refreshKey]);

  // Patch in WS presence events without refetching — instant feel.
  React.useEffect(() => {
    if (!lastPresence || error) return;
    setData((prev) => {
      if (!prev) return prev;
      const viewers = [...(prev.viewers || [])];
      let anon = prev.anonymous || 0;
      const total = (prev.total_connections || 0);

      if (lastPresence.event === "join") {
        if (lastPresence.user_id) {
          // Dedupe by user_id (multi-tab from same user).
          if (!viewers.some((v) => v.user_id === lastPresence.user_id)) {
            viewers.push({
              conn_id: lastPresence.conn_id || `ws_${lastPresence.user_id}`,
              user_id: lastPresence.user_id,
              user_name: lastPresence.user_name,
              username: lastPresence.username,
              picture: lastPresence.picture,
              is_seller: !!lastPresence.is_seller,
              is_admin: !!lastPresence.is_admin,
              joined_at: lastPresence.ts,
            });
          }
        } else {
          anon += 1;
        }
        return {
          ...prev,
          viewers,
          anonymous: anon,
          unique_users: viewers.length,
          total_connections: Math.max(total + 1, viewers.length + anon),
        };
      }
      if (lastPresence.event === "leave") {
        if (lastPresence.user_id) {
          const idx = viewers.findIndex((v) => v.user_id === lastPresence.user_id);
          if (idx >= 0) viewers.splice(idx, 1);
        } else if (anon > 0) {
          anon -= 1;
        }
        return {
          ...prev,
          viewers,
          anonymous: anon,
          unique_users: viewers.length,
          total_connections: Math.max(0, total - 1),
        };
      }
      return prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastPresence?.event, lastPresence?.ts, lastPresence?.user_id]);

  return (
    <div className="flex-1 overflow-y-auto p-3" data-testid="watching-tab">
      {error ? (
        <div className="text-center py-10 px-4">
          <div className="font-display font-bold text-lg text-zinc-900 mb-1">{liveCount} watching</div>
          <p className="text-xs text-zinc-500">{error}</p>
        </div>
      ) : !data ? (
        <p className="text-xs text-zinc-400 text-center py-8">Loading…</p>
      ) : (
        <div className="space-y-2">
          {/* Live counter so the seller can quickly verify list ↔ count parity. */}
          <div className="flex items-center justify-between px-2 pb-2 border-b border-zinc-100 mb-1">
            <div className="text-xs text-zinc-500 font-mono uppercase tracking-wider">
              {(data.unique_users || 0) + (data.anonymous || 0)} live
            </div>
            <button
              type="button"
              onClick={refetch}
              className="text-[10px] font-mono uppercase tracking-wider text-[#1D78AB] hover:underline"
              data-testid="watching-refresh"
            >
              refresh
            </button>
          </div>
          {data.viewers.map((v) => (
            <div
              key={v.conn_id}
              className="flex items-center gap-3 px-2 py-2 rounded-xl hover:bg-zinc-50"
              data-testid={`watching-row-${v.user_id}`}
            >
              <SellerAvatar name={v.user_name || "Guest"} picture={v.picture} size={32} />
              <div className="flex-1 min-w-0">
                <UserNamePopover
                  userId={v.user_id}
                  username={v.username || v.user_name}
                  displayName={v.user_name}
                  onReply={onReply}
                  isStreamMod={isStreamMod}
                  streamId={streamId}
                  sellerId={sellerId}
                  currentUserId={currentUserId}
                  className="text-sm font-semibold text-zinc-900 truncate hover:text-[#1D78AB]"
                  testid={`viewer-popover-${v.user_id}`}
                >
                  {v.user_name || "Guest"}
                </UserNamePopover>
                {v.username && <div className="text-[11px] text-zinc-500">@{v.username}</div>}
              </div>
              {v.is_seller && (
                <span className="text-[10px] font-bold uppercase tracking-wider text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">
                  Seller
                </span>
              )}
              {!v.is_seller && streamMods.some((m) => m.user_id === v.user_id) && (
                <span className="text-[10px] font-bold uppercase tracking-wider text-white bg-[#1D78AB] px-2 py-0.5 rounded-full">
                  Mod
                </span>
              )}
            </div>
          ))}
          {data.anonymous > 0 && (
            <div className="text-[11px] text-zinc-500 text-center pt-2">
              + {data.anonymous} anonymous watcher{data.anonymous === 1 ? "" : "s"}
            </div>
          )}
        </div>
      )}
    </div>
  );
}



/**
 * live-shopping-style rail button: large white icon stacked on top of a
 * small white text label. No background pill — the icon is visible
 * directly on the video, with a parent drop-shadow ensuring legibility
 * on any background (light face, dark wall, etc.).
 */
function RailButton({ icon, label, onClick, testid, badge }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="relative flex flex-col items-center gap-1 active:scale-95 transition-transform group focus:outline-none"
      data-testid={testid}
    >
      {/* Uniform 44×44 circular icon bubble — every rail tile (RailButton,
          Detect, Cinema badge) uses the same dimensions so the column
          aligns perfectly down the entire side rail. The dark glass
          background + soft border read clearly over any live video
          backdrop. */}
      <span className="relative h-11 w-11 rounded-full grid place-items-center text-white bg-zinc-900/55 backdrop-blur-sm border border-white/15 shadow-md">
        {icon}
        {typeof badge === "number" && badge > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold grid place-items-center">
            {badge > 99 ? "99+" : badge}
          </span>
        )}
      </span>
      <span className="text-[11px] font-semibold text-white tracking-tight leading-none">
        {label}
      </span>
    </button>
  );
}

/**
 * One tile inside the mobile "More" bottom sheet.  Bigger hit-targets than
 * the side rail buttons, with a soft rounded surface — closer to the way
 * live sale platforms/IG present overflow actions on phones.
 */
function MoreTile({ icon, label, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex-1 min-w-0 flex flex-col items-center gap-1.5 px-1 py-2.5 rounded-2xl bg-zinc-100 hover:bg-zinc-200 active:scale-95 transition"
      data-testid={testid}
    >
      <span className="h-10 w-10 grid place-items-center rounded-full bg-zinc-900 text-white">
        {icon}
      </span>
      <span className="text-[10px] font-semibold text-zinc-800 tracking-tight w-full truncate text-center">
        {label}
      </span>
    </button>
  );
}


/**
 * One row of the live activity ticker. Slides in from the left, holds for a
 * moment, then fades out — driven entirely by CSS animations so we don't
 * need a heavy motion library. The colored leading dot encodes the event
 * kind (bid → amber, like → red, join → emerald, tip → indigo).
 */
function ActivityTickerRow({ text, kind }) {
  const dotClass =
    {
      bid:  "bg-amber-300",
      like: "bg-red-400",
      join: "bg-emerald-300",
      tip:  "bg-indigo-300",
    }[kind] || "bg-white/80";
  return (
    <div
      className="inline-flex items-center gap-2 max-w-[80%] rounded-full bg-zinc-900/65 backdrop-blur-sm border border-white/10 px-3 py-1.5 text-white text-[12px] font-semibold animate-activity-row"
      data-testid={`activity-row-${kind}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${dotClass} animate-pulse`} />
      <span className="truncate">{text}</span>
    </div>
  );
}



/**
 * Hard shutdown view: rendered when stream.status === "ended". Replaces
 * the entire live page so nobody — viewers, founders, or even the seller —
 * can see the chat, video, or active lots after the show has been ended.
 * The seller can still get to their post-show summary via /dashboard.
 */
function AgeGate18({ stream, onConfirm }) {
  const goBack = () => {
    if (typeof window !== "undefined") {
      if (window.history.length > 1) window.history.back();
      else window.location.href = "/live";
    }
  };
  return (
    <div
      className="fixed inset-0 z-[200] bg-zinc-950/95 flex items-center justify-center p-6"
      data-testid="stream-age-gate"
      style={{ height: "100dvh" }}
    >
      <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl overflow-hidden">
        <div className="bg-rose-600 text-white px-6 py-5 flex items-center gap-3">
          <span className="text-2xl font-black tracking-widest bg-white text-rose-600 rounded-md px-2 py-0.5">
            18+
          </span>
          <div>
            <p className="font-display font-black text-lg leading-tight">Adults only</p>
            <p className="text-xs opacity-90">{stream.seller_name} marked this show 18+</p>
          </div>
        </div>
        <div className="px-6 py-6">
          <p className="text-sm text-zinc-700 leading-relaxed">
            This live show is restricted to adults aged <b>18 years or older</b>.
            By continuing you confirm that you are at least 18 years of age and
            agree to view age-restricted content on Bidorah.
          </p>
          <div className="mt-6 grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={goBack}
              className="h-11 rounded-full border border-zinc-300 hover:bg-zinc-50 font-bold text-sm text-zinc-700"
              data-testid="stream-age-gate-leave"
            >
              Take me back
            </button>
            <button
              type="button"
              onClick={onConfirm}
              className="h-11 rounded-full bg-rose-600 hover:bg-rose-700 font-bold text-sm text-white"
              data-testid="stream-age-gate-confirm"
            >
              I'm 18 or older
            </button>
          </div>
          <p className="text-[10px] text-zinc-400 mt-4 leading-snug text-center">
            Falsely confirming your age violates Bidorah's Terms of Service and
            may result in permanent account removal.
          </p>
        </div>
      </div>
    </div>
  );
}


function EndedStreamLockout({ stream }) {
  const navigate = useNavigate();
  const endedBy = stream.ended_by_admin ? "an admin" : "the seller";
  return (
    <div className="min-h-screen bg-zinc-950 text-white flex flex-col" data-testid="ended-stream-lockout">
      <div className="hidden md:block">
        <Navbar />
      </div>
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="max-w-md text-center space-y-5">
          <div className="mx-auto h-16 w-16 rounded-full bg-zinc-900 border border-white/10 grid place-items-center">
            <BroadcastIcon size={28} className="text-zinc-500" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.22em] font-mono text-zinc-500 mb-1.5">
              Stream Ended
            </div>
            <h1 className="font-display font-black text-2xl md:text-3xl leading-tight">
              {stream.title || "This live show"}
            </h1>
            <p className="text-zinc-400 text-sm mt-3 leading-relaxed">
              {stream.seller_name ? (
                <span className="inline-flex items-center gap-1 align-baseline">
                  {stream.seller_name}
                  <VerifiedBadge visible={stream.seller_verified_badge} size={12} />{" "}
                </span>
              ) : null}
              ended this live show. It's been sealed by {endedBy} and can't be
              re-entered. New shows from this seller will appear in your feed
              the moment they go live.
            </p>
          </div>
          <div className="flex items-center justify-center gap-2 flex-wrap">
            <Button
              onClick={() => navigate("/live")}
              className="bg-[#1D78AB] hover:bg-[#165d85] rounded-full px-5"
              data-testid="ended-stream-back-to-live"
            >
              Browse live shows
            </Button>
            {stream.seller_id && (
              <Button
                variant="outline"
                onClick={() => navigate(`/seller/${stream.seller_id}`)}
                className="rounded-full border-white/20 text-white hover:bg-white/10"
                data-testid="ended-stream-seller-link"
              >
                Visit seller
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Admin entry sparkle overlay. Whenever a `presence` event with
 * `is_admin: true` and `event: "join"` arrives, every viewer in the room
 * sees a brief sparkle burst near the top of the video stage. Purely
 * decorative — signals "an admin just walked in" without exposing PII.
 */
function AdminEntrySparkle({ trigger }) {
  const [bursts, setBursts] = React.useState([]);

  React.useEffect(() => {
    if (!trigger) return;
    const id = `${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
    setBursts((prev) => [...prev, id]);
    const t = setTimeout(() => {
      setBursts((prev) => prev.filter((b) => b !== id));
    }, 3600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger?.id]);

  // Always-mounted wrapper so the data-testid stays in the DOM for testing
  // / observability tools, even between bursts.
  return (
    <div
      className="pointer-events-none absolute inset-0 z-[35] overflow-hidden"
      data-testid="admin-entry-sparkle"
      data-active={bursts.length > 0 ? "1" : "0"}
    >
      {bursts.map((id) => (
        <div key={id} className="absolute inset-0">
          {/* Centerpiece sparkle ribbon */}
          <div className="absolute left-1/2 top-[18%] -translate-x-1/2 flex items-center gap-2 animate-admin-flair-rise">
            <SparkleIcon size={20} weight="fill" className="text-amber-300 animate-pulse" />
            <span className="rounded-full bg-gradient-to-r from-amber-400 via-yellow-300 to-amber-400 text-amber-950 px-3.5 py-1.5 text-xs font-black tracking-wider uppercase shadow-[0_8px_30px_rgba(251,191,36,0.55)]">
              Admin entered
            </span>
            <SparkleIcon size={20} weight="fill" className="text-amber-300 animate-pulse" />
          </div>
          {/* Side sparkle field — eight little dots radiating out */}
          {[...Array(8)].map((_, i) => (
            <SparkleIcon
              key={i}
              size={14}
              weight="fill"
              className="absolute text-amber-300 animate-admin-flair-spark"
              style={{
                left: `${15 + (i * 9) + (i % 2 === 0 ? 0 : 3)}%`,
                top: `${22 + (i % 2) * 8}%`,
                animationDelay: `${i * 90}ms`,
                opacity: 0.85,
              }}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

export { EndedStreamLockout, AdminEntrySparkle };
